import { useState, useCallback, useRef, useEffect, useMemo, memo } from "react";
import { API_BASE, submitQuery, pollTask, downloadReport, healthCheck, fetchDatasources, FALLBACK_DATASOURCES, type TaskStatus, type DataSourceProgress as DSProgressType, type DatasourceConfig, type DatasourcesResponse } from "./lib/api";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { useQueryHistory } from "./hooks/useQueryHistory";
import DosageFormDropdown, { type DosageForm, DOSAGE_FORMS } from "./components/DosageFormDropdown";
import DrugInputWithSuggestions, { type ExtendedDrugResult } from "./components/DrugInputWithSuggestions";
import { type DosageFormMatchResult } from "./services/dosageFormExtractor";
import SpecificationInput from "./components/SpecificationInput";
import QueryHistoryList from "./components/QueryHistoryList";
import DownloadPanel from "./components/DownloadPanel";
import ProgressGrid, { type ProgressGridSource, DATASOURCE_NAMES, DATASOURCE_REGIONS, DATASOURCE_ICONS } from "./components/ProgressGrid";
import SummaryPreviewCard, { type SummaryPreviewCardProps } from "./components/SummaryPreviewCard";
import BioanalysisPhaseOne from "./components/BioanalysisPhaseOne";
import BioanalysisPhaseTwo from "./components/BioanalysisPhaseTwo";
import { buildPhaseOneAnalysis } from "./lib/phaseOneAnalysis";
import { buildBioanalysisPhaseOne } from "./lib/bioanalysisPhaseOne";
import { buildBioanalysisPhaseTwo } from "./lib/bioanalysisPhaseTwo";
import GlobalLoading from "./components/GlobalLoading";
import UserFriendlyError, { categorizeError } from "./components/UserFriendlyError";
import { type DrugMapping, drugMappings } from "./lib/drugMappings";
import { validateSpecification } from "./lib/specification";
import { alignMarkdownRow, parseMarkdownRow } from "./lib/markdownTable";
import { buildCsv, getSourceUrl, safeDownloadName } from "./lib/resultActions";
import { Atom, ExternalLink, FileText } from "lucide-react";

type Stage = "submit" | "progress" | "result";
type ResultTab = "all" | "success" | "issues";

function formatDataForDisplay(data: any): string {
  if (data === null || data === undefined) {
    return '';
  }
  
  if (typeof data === 'object' && data !== null) {
    try {
      return JSON.stringify(data, null, 2);
    } catch (e) {
      return String(data);
    }
  }
  
  if (Array.isArray(data)) {
    return data.join(', ');
  }
  
  return String(data);
}

interface FormErrors {
  drugName?: string;
  dosageForm?: string;
  specification?: string;
  datasources?: string;
}

function DrugChip({ drug }: { drug: DrugMapping | null }) {
  if (!drug) return null;
  return (
    <span className="inline-flex items-baseline gap-2">
      <span className="font-semibold text-foreground">{drug.nameZh}</span>
      <span className="text-sm text-muted-foreground">({drug.nameEn})</span>
    </span>
  );
}

function toDrugMapping(drug: Partial<DrugMapping> & { id: string; nameZh: string; nameEn: string }): DrugMapping {
  return {
    id: drug.id,
    nameZh: drug.nameZh,
    nameEn: drug.nameEn,
    inn: drug.inn || drug.nameEn,
    aliasesZh: drug.aliasesZh || [],
    aliasesEn: drug.aliasesEn || [],
    pinyin: drug.pinyin || [],
    pinyinAbbr: drug.pinyinAbbr || "",
    category: drug.category || "",
    therapeuticClass: drug.therapeuticClass || "",
  };
}

type SelectableDrug = DrugMapping | ExtendedDrugResult;

function normalizeIdentityValues(values: Array<string | undefined | null> = [], limit = 8): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const text = String(value ?? "").replace(/\s+/g, " ").trim();
    if (!text || text === "—" || text === "-") return;
    const key = text.toLocaleLowerCase().replace(/\s+/g, "");
    if (seen.has(key)) return;
    seen.add(key);
    result.push(text);
  });
  return result.slice(0, limit);
}

function drugSourceLabel(source?: string): string {
  const labels: Record<string, string> = {
    local: "本地药品库",
    identity: "后端身份识别",
    cn_to_inn: "中文名映射",
    cn_alias_to_inn: "中文别名映射",
    extended_cn_to_inn: "扩展中文映射",
    extended_cn_alias_to_inn: "扩展中文别名",
    embedded_latin: "中英混合识别",
    brand: "商品名识别",
    cn_combination: "复方识别",
    normalize_drug_name: "药名标准化",
    normalize_drug_query: "查询词标准化",
    inn: "INN 数据库",
    matcher: "智能匹配",
    manual: "手动输入",
    external: "外部数据源",
  };
  return labels[source || ""] || source || "未知来源";
}

function isLikelyCombination(nameEn: string, components: string[] = []): boolean {
  return components.length > 1 || /[+/]|(?:\band\b)|(?:_and_)/i.test(nameEn);
}

function toDrugIdentityResult(drug: SelectableDrug, mappedDrug?: DrugMapping): ExtendedDrugResult {
  const mapped = mappedDrug || toDrugMapping(drug);
  const extended = drug as Partial<ExtendedDrugResult>;
  const components = normalizeIdentityValues(
    extended.components && extended.components.length > 0
      ? extended.components
      : [mapped.inn || mapped.nameEn],
    12,
  );
  const componentsCn = normalizeIdentityValues(
    extended.componentsCn && extended.componentsCn.length > 0
      ? extended.componentsCn
      : [mapped.nameZh],
    12,
  );
  const queryTermsEn = normalizeIdentityValues([
    ...(extended.queryTermsEn || []),
    mapped.inn,
    mapped.nameEn,
    ...(mapped.aliasesEn || []),
  ]);
  const queryTermsCn = normalizeIdentityValues([
    ...(extended.queryTermsCn || []),
    mapped.nameZh,
    ...(mapped.aliasesZh || []),
  ]);

  return {
    id: mapped.id,
    nameZh: mapped.nameZh,
    nameEn: mapped.nameEn,
    inn: mapped.inn || mapped.nameEn,
    category: mapped.category,
    therapeuticClass: mapped.therapeuticClass,
    aliasesZh: mapped.aliasesZh,
    aliasesEn: mapped.aliasesEn,
    components,
    componentsCn,
    queryTermsEn,
    queryTermsCn,
    isCombination: Boolean(extended.isCombination) || isLikelyCombination(mapped.inn || mapped.nameEn, components),
    warnings: normalizeIdentityValues(extended.warnings || [], 4),
    source: extended.source || "local",
    confidence: typeof extended.confidence === "number" ? extended.confidence : 1,
  };
}

function IdentityTokenList({ values }: { values: string[] }) {
  if (values.length === 0) {
    return <span className="text-sm text-muted-foreground">待后端按输入实时识别</span>;
  }
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((value) => (
        <span key={value} className="rounded-md border border-border bg-background px-2 py-1 text-xs font-medium text-foreground">
          {value}
        </span>
      ))}
    </div>
  );
}

function DrugIdentityPreview({ identity, drug }: { identity: ExtendedDrugResult | null; drug: DrugMapping | null }) {
  if (!identity || !drug) return null;
  const confidence = Math.round((identity.confidence || 0) * 100);
  const components = normalizeIdentityValues(identity.components || [drug.inn || drug.nameEn], 12);
  const componentsCn = normalizeIdentityValues(identity.componentsCn || [drug.nameZh], 12);
  const componentRows = Array.from({ length: Math.max(components.length, componentsCn.length) })
    .map((_, index) => ({
      cn: componentsCn[index] || "",
      en: components[index] || "",
    }))
    .filter((item) => item.cn || item.en);
  const queryTermsCn = normalizeIdentityValues(identity.queryTermsCn || [drug.nameZh]);
  const queryTermsEn = normalizeIdentityValues(identity.queryTermsEn || [identity.inn || drug.nameEn]);
  const confidenceClass = confidence >= 85
    ? "border-success/25 bg-success/10 text-success"
    : confidence >= 60
      ? "border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300"
      : "border-destructive/20 bg-destructive/10 text-destructive";

  return (
    <div className="mt-3 rounded-lg border border-primary/20 bg-primary/5 p-4 animate-fadeIn">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="text-sm font-semibold text-foreground">药品身份已标准化</div>
          <div className="mt-1 text-xs text-muted-foreground">
            来源：{drugSourceLabel(identity.source)} · {identity.isCombination ? "复方制剂" : "单方制剂"}
          </div>
        </div>
        <span className={`w-fit rounded-md border px-2.5 py-1 text-xs font-semibold ${confidenceClass}`}>
          识别置信度 {confidence}%
        </span>
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <div className="rounded-md border border-border/70 bg-background/70 p-3">
          <div className="text-xs text-muted-foreground">中文标准名</div>
          <div className="mt-1 text-sm font-semibold text-foreground">{drug.nameZh}</div>
        </div>
        <div className="rounded-md border border-border/70 bg-background/70 p-3">
          <div className="text-xs text-muted-foreground">英文 / INN</div>
          <div className="mt-1 break-words text-sm font-semibold text-foreground">{identity.inn || drug.nameEn}</div>
        </div>
        <div className="rounded-md border border-border/70 bg-background/70 p-3">
          <div className="text-xs text-muted-foreground">提交查询名</div>
          <div className="mt-1 break-words text-sm font-semibold text-foreground">{drug.nameEn}</div>
        </div>
      </div>

      {componentRows.length > 0 && (
        <div className="mt-4">
          <div className="mb-2 text-xs font-medium text-muted-foreground">活性成分</div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {componentRows.map((item, index) => (
              <div key={`${item.cn}-${item.en}-${index}`} className="rounded-md border border-border/70 bg-background/70 px-3 py-2 text-sm">
                <span className="font-medium text-foreground">{item.cn || item.en}</span>
                {item.cn && item.en && item.cn !== item.en && (
                  <span className="ml-2 text-muted-foreground">{item.en}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">中文数据源检索词</div>
          <IdentityTokenList values={queryTermsCn} />
        </div>
        <div>
          <div className="mb-2 text-xs font-medium text-muted-foreground">海外数据源检索词</div>
          <IdentityTokenList values={queryTermsEn} />
        </div>
      </div>

      {identity.warnings && identity.warnings.length > 0 && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
          {identity.warnings.join("；")}
        </div>
      )}
    </div>
  );
}

function ReferenceDrugTable({ drugs }: { drugs: Record<string, unknown>[] }) {
  if (!drugs || drugs.length === 0) return null;
  const sourceColorMap: Record<string, string> = {
    drugfuture: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300",
    yaozh: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300",
    builtin: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    builtin_file: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  };
  const sourceLabelMap: Record<string, string> = {
    drugfuture: "drugfuture",
    yaozh: "药智网",
    builtin: "内置缓存",
    builtin_file: "文件缓存",
  };
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
      <table className="min-w-full text-xs sm:text-sm">
        <thead>
          <tr className="bg-muted/50 sticky top-0">
            <th className="px-2 py-2.5 text-left font-medium text-muted-foreground whitespace-nowrap">#</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap">药品名称</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap hidden md:table-cell">英文名称</th>
            <th className="px-2 py-2.5 text-center font-medium text-foreground whitespace-nowrap">剂型</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap">规格</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap hidden lg:table-cell">持证商</th>
            <th className="px-2 py-2.5 text-left font-medium text-foreground whitespace-nowrap hidden xl:table-cell">备注</th>
            <th className="px-2 py-2.5 text-center font-medium text-foreground whitespace-nowrap">状态</th>
            <th className="px-2 py-2.5 text-center font-medium text-foreground whitespace-nowrap">来源</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {drugs.map((drug, idx) => {
            const src = String(drug.source || drug.source_name || "—");
            const matchType = String(drug.match_type || "exact");
            const status = String(drug.approval_status || "—");
            const isRef = status === "正式通稿";
            return (
              <tr key={idx} className={`hover:bg-muted/30 transition-colors ${matchType === "reference" ? "opacity-70" : ""}`}>
                <td className="px-2 py-2 text-muted-foreground">{idx + 1}</td>
                <td className="px-2 py-2 font-medium text-foreground max-w-[160px] truncate" title={String(drug.drug_name || "")}>
                  {String(drug.drug_name || "—")}
                </td>
                <td className="px-2 py-2 text-muted-foreground max-w-[180px] truncate hidden md:table-cell" title={String(drug.english_name || "")}>
                  {String(drug.english_name || "—")}
                </td>
                <td className="px-2 py-2 text-center">
                  <span className="inline-block px-1.5 py-0.5 rounded bg-secondary text-secondary-foreground text-xs">
                    {String(drug.dosage_form || "—")}
                  </span>
                </td>
                <td className="px-2 py-2 text-foreground font-mono text-xs">{String(drug.specification || "—")}</td>
                <td className="px-2 py-2 text-muted-foreground max-w-[180px] truncate hidden lg:table-cell" title={String(drug.holder || "")}>
                  {String(drug.holder || "—")}
                </td>
                <td className="px-2 py-2 text-muted-foreground max-w-[150px] truncate hidden xl:table-cell" title={String(drug.source_type || "")}>
                  {String(drug.source_type || "—").slice(0, 20)}
                </td>
                <td className="px-2 py-2 text-center">
                  {isRef ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-success/10 text-success text-xs font-medium">
                      ✓ 正式
                    </span>
                  ) : status !== "—" && status !== "" ? (
                    <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-warning/10 text-warning text-xs">
                      {status.slice(0, 4)}
                    </span>
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </td>
                <td className="px-2 py-2 text-center">
                  <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${sourceColorMap[src] || "bg-gray-100 text-gray-600"}`}>
                    {sourceLabelMap[src] || src}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatFdaDate(value: unknown): string {
  const text = String(value || "");
  return /^\d{8}$/.test(text)
    ? `${text.slice(0, 4)}-${text.slice(4, 6)}-${text.slice(6, 8)}`
    : text || "-";
}

function fdaReviewText(value: unknown, fallback = "-"): string {
  if (Array.isArray(value)) {
    const joined = value.map((item) => String(item || "").trim()).filter(Boolean).join(" / ");
    return joined || fallback;
  }
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  return text || fallback;
}

function fdaReviewCategoryLabel(document: Record<string, unknown>): string {
  return (
    fdaReviewText(document.display_category, "") ||
    fdaReviewText(document.review_category, "") ||
    fdaReviewText(document.target_pdf_type, "") ||
    fdaReviewText(document.document_type, "") ||
    "FDA Review"
  );
}

function fdaReviewEnglishCategory(document: Record<string, unknown>): string {
  return (
    fdaReviewText(document.professional_category, "") ||
    fdaReviewText(document.target_pdf_type, "") ||
    fdaReviewText(document.review_category, "") ||
    fdaReviewText(document.document_type, "")
  );
}

function fdaReviewDomain(document: Record<string, unknown>): string {
  return fdaReviewText(document.domain, "") || "Review";
}

function fdaReviewClinicalValue(document: Record<string, unknown>): string {
  const explicitValue = fdaReviewText(document.clinical_value, "");
  if (explicitValue) return explicitValue;

  const category = fdaReviewEnglishCategory(document).toLowerCase();
  if (category.includes("clinical pharmacology")) {
    return "优先用于剂量选择、食物影响、DDI、特殊人群和BE/PK依据判断";
  }
  if (category.includes("label")) {
    return "用于确认适应症、用法用量、临床药理、PK参数和关键安全性信息";
  }
  if (category.includes("clinical review")) {
    return "用于查看关键临床试验设计、终点、人群、疗效和安全性审评意见";
  }
  if (category.includes("quality") || category.includes("chemistry")) {
    return "用于补充剂型、处方、规格、质量属性和仿制药开发背景";
  }
  if (category.includes("statistical")) {
    return "用于参考统计模型、终点分析、样本量和敏感性分析线索";
  }
  if (category.includes("risk")) {
    return "用于识别风险控制、REMS、安全性重点和上市后风险管理信息";
  }
  return "用于补充审评结论、获益风险判断和跨学科摘要";
}

function fdaReviewDocumentsForDisplay(review: Record<string, unknown>): Record<string, unknown>[] {
  const keyDocuments = recordArray(review.key_review_documents);
  if (keyDocuments.length > 0) return keyDocuments;

  const targetDocuments = recordArray(review.target_review_pdfs);
  if (targetDocuments.length > 0) return targetDocuments;

  return recordArray(review.review_documents).filter((document) => Boolean(document.is_pdf));
}

function fdaReviewApplicationSummaries(
  review: Record<string, unknown>,
  documents: Record<string, unknown>[],
): Record<string, unknown>[] {
  const summaries = recordArray(review.review_document_summary);
  if (summaries.length > 0) return summaries;

  const applications = recordArray(review.applications);
  if (applications.length > 0) {
    return applications.map((application) => {
      const applicationNumber = String(application.application_number || "");
      const docs = documents.filter(
        (document) => String(document.application_number || "") === applicationNumber,
      );
      return {
        ...application,
        key_document_count: docs.length,
        key_document_categories: Array.from(new Set(docs.map(fdaReviewCategoryLabel))),
      };
    });
  }

  return Array.from(new Set(documents.map((document) => String(document.application_number || ""))))
    .filter(Boolean)
    .map((applicationNumber) => ({
      application_number: applicationNumber,
      key_document_count: documents.filter(
        (document) => String(document.application_number || "") === applicationNumber,
      ).length,
      key_document_categories: [],
      overview_url: "",
      brand_names: [],
      active_ingredients: [],
      sponsor_name: "",
    }));
}

function fdaReviewRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown>
    : {};
}

function fdaReviewPageUrl(url: unknown, page: unknown): string {
  const sourceUrl = String(url || "").trim();
  const pageNumber = Number(page || 0);
  if (!sourceUrl) return "";
  if (!Number.isFinite(pageNumber) || pageNumber <= 0) return sourceUrl;
  return `${sourceUrl.split("#")[0]}#page=${Math.trunc(pageNumber)}`;
}

function FdaPkSymbol({ symbol }: { symbol: string }) {
  const normalized = symbol.replace(/\s+/g, "");
  const symbolMap: Record<string, { base: string; sub?: string; suffix?: string }> = {
    Cmax: { base: "C", sub: "max" },
    Cmin: { base: "C", sub: "min" },
    Tmax: { base: "T", sub: "max" },
    "t1/2": { base: "t", sub: "1/2" },
    "AUC0-t": { base: "AUC", sub: "0-t" },
    "AUC0-inf": { base: "AUC", sub: "0-inf" },
    "AUC0-∞": { base: "AUC", sub: "0-∞" },
    "AUCtau": { base: "AUC", sub: "tau" },
    "CL/F": { base: "CL/F" },
    "Vd/F": { base: "V", sub: "d", suffix: "/F" },
    Rac: { base: "R", sub: "ac" },
  };
  const parts = symbolMap[normalized];
  if (!parts) return <i>{symbol}</i>;
  return (
    <span className="whitespace-nowrap font-semibold text-foreground">
      <i>{parts.base}</i>
      {parts.sub && <sub>{parts.sub}</sub>}
      {parts.suffix && <i>{parts.suffix}</i>}
    </span>
  );
}

function FdaReviewEvidenceLink({
  url,
  page,
  label,
}: {
  url: unknown;
  page: unknown;
  label?: string;
}) {
  const href = fdaReviewPageUrl(url, page);
  if (!href) return <span className="text-muted-foreground">-</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:underline"
    >
      {label || `PDF 第 ${String(page || "-")} 页`}
      <ExternalLink className="h-3 w-3" aria-hidden="true" />
    </a>
  );
}

function FdaReviewPkEvidence({ evidence }: { evidence: Record<string, unknown> }) {
  const status = String(evidence.status || "");
  const studies = recordArray(evidence.studies);
  const parameters = recordArray(evidence.parameter_summary);
  const documents = recordArray(evidence.documents_analyzed);
  const gaps = Array.isArray(evidence.evidence_gaps)
    ? evidence.evidence_gaps.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const managerNotes = Array.isArray(evidence.manager_notes)
    ? evidence.manager_notes.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const processing = fdaReviewRecord(evidence.processing);
  const topics = fdaReviewRecord(evidence.topic_summaries);
  const topicDefinitions = [
    ["food_effect", "食物影响"],
    ["ddi", "药物相互作用（DDI）"],
    ["special_populations", "特殊人群"],
    ["dose_proportionality", "剂量比例关系"],
    ["accumulation", "蓄积与稳态"],
    ["mass_balance", "物料平衡/代谢"],
  ] as const;
  const topicRows: Array<Record<string, unknown> & { label: string }> = topicDefinitions.flatMap(([key, label]) =>
    recordArray(topics[key]).map((item) => ({ ...item, label })),
  );
  const unavailable = ["disabled", "not_available", "error"].includes(status);
  const statusLabel = status === "success"
    ? "已提取"
    : status === "partial"
      ? "部分证据"
      : "需人工复核";
  const statusClass = status === "success"
    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
    : "bg-amber-500/10 text-amber-700 dark:text-amber-300";

  return (
    <section aria-labelledby="fda-review-pk-heading" className="border-t border-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 id="fda-review-pk-heading" className="text-sm font-semibold text-foreground">
            临床药代动力学证据摘要
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            FDA 审评文件自动摘取，供方案可行性、关键终点和资料缺口初筛
          </p>
        </div>
        <span className={`rounded-sm px-2 py-1 text-xs font-semibold ${statusClass}`}>
          {statusLabel}
        </span>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-foreground">
        {fdaReviewText(evidence.executive_summary, "暂未形成可审计的临床 PK 摘要。")}
      </p>

      {!unavailable && (
        <dl className="mt-3 grid grid-cols-2 border-y border-border bg-muted/10 text-xs sm:grid-cols-4">
          <div className="px-3 py-2.5">
            <dt className="text-muted-foreground">分析文件</dt>
            <dd className="mt-1 font-semibold text-foreground">{documents.length}</dd>
          </div>
          <div className="border-l border-border px-3 py-2.5">
            <dt className="text-muted-foreground">研究线索</dt>
            <dd className="mt-1 font-semibold text-foreground">{studies.length}</dd>
          </div>
          <div className="border-l border-border px-3 py-2.5">
            <dt className="text-muted-foreground">参数证据</dt>
            <dd className="mt-1 font-semibold text-foreground">{parameters.length}</dd>
          </div>
          <div className="border-l border-border px-3 py-2.5">
            <dt className="text-muted-foreground">相关页数</dt>
            <dd className="mt-1 font-semibold text-foreground">
              {String(processing.pages_with_evidence ?? processing.pages_relevant ?? 0)}
            </dd>
          </div>
        </dl>
      )}

      {studies.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-xs font-semibold text-foreground">临床 PK / 生物药剂学研究</h4>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-[980px] w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">研究 / 类型</th>
                  <th className="px-3 py-2 text-left font-medium">设计与人群</th>
                  <th className="px-3 py-2 text-left font-medium">给药</th>
                  <th className="px-3 py-2 text-left font-medium">关键参数 / 结论</th>
                  <th className="px-3 py-2 text-right font-medium">证据出处</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {studies.map((study, index) => {
                  const source = fdaReviewRecord(study.source);
                  const pages = Array.isArray(source.pages) ? source.pages : [];
                  const firstPage = pages[0];
                  const lastPage = pages[pages.length - 1];
                  const pageLabel = pages.length > 1
                    ? `PDF 第 ${String(firstPage)}–${String(lastPage)} 页`
                    : `PDF 第 ${String(firstPage || "-")} 页`;
                  const findings = Array.isArray(study.key_findings)
                    ? study.key_findings.map((item) => String(item || "")).filter(Boolean)
                    : [];
                  const pkParameters = Array.isArray(study.pk_parameters)
                    ? study.pk_parameters.map((item) => String(item || "")).filter(Boolean)
                    : [];
                  return (
                    <tr key={`${String(study.study_id || "study")}-${index}`} className="align-top hover:bg-muted/20">
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-foreground">{fdaReviewText(study.study_id, `研究 ${index + 1}`)}</div>
                        {study.study_title && (
                          <div className="mt-1 max-w-[260px] leading-relaxed text-foreground">
                            {fdaReviewText(study.study_title)}
                          </div>
                        )}
                        <div className="mt-1 text-muted-foreground">{fdaReviewText(study.study_type)}</div>
                      </td>
                      <td className="max-w-[330px] px-3 py-2.5 leading-relaxed text-muted-foreground">
                        <div>{fdaReviewText(study.design)}</div>
                        <div className="mt-1">
                          {fdaReviewText(study.population, "人群未自动确认")}
                          {study.sample_size ? `；N=${String(study.sample_size)}` : ""}
                        </div>
                      </td>
                      <td className="max-w-[180px] px-3 py-2.5 text-muted-foreground">
                        {fdaReviewText(study.dosing, "需查阅原文")}
                      </td>
                      <td className="max-w-[360px] px-3 py-2.5 leading-relaxed text-muted-foreground">
                        {pkParameters.length > 0 && (
                          <div className="flex flex-wrap gap-x-2 gap-y-1 text-foreground">
                            {pkParameters.map((symbol) => <FdaPkSymbol key={symbol} symbol={symbol} />)}
                          </div>
                        )}
                        {findings.slice(0, 2).map((finding) => (
                          <p key={finding} className="mt-1">{finding}</p>
                        ))}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <FdaReviewEvidenceLink
                          url={source.url}
                          page={firstPage}
                          label={pageLabel}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {parameters.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-xs font-semibold text-foreground">关键 PK 参数证据</h4>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-[880px] w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">参数</th>
                  <th className="px-3 py-2 text-left font-medium">数值 / 单位</th>
                  <th className="px-3 py-2 text-left font-medium">原文证据</th>
                  <th className="px-3 py-2 text-right font-medium">页码</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {parameters.map((parameter, index) => (
                  <tr key={`${String(parameter.symbol || "parameter")}-${String(parameter.page || index)}-${index}`} className="align-top hover:bg-muted/20">
                    <td className="px-3 py-2.5">
                      <FdaPkSymbol symbol={fdaReviewText(parameter.symbol, "PK")} />
                      <div className="mt-1 text-[11px] text-muted-foreground">{fdaReviewText(parameter.display_name, "")}</div>
                      {parameter.study_id && (
                        <div className="mt-1 text-[11px] text-primary">{fdaReviewText(parameter.study_id)}</div>
                      )}
                    </td>
                    <td className="px-3 py-2.5 font-medium text-foreground">
                      {parameter.value
                        ? `${String(parameter.value)}${parameter.unit ? ` ${String(parameter.unit)}` : ""}`
                        : "原文提及，数值待复核"}
                    </td>
                    <td className="max-w-[520px] px-3 py-2.5 leading-relaxed text-muted-foreground">
                      {fdaReviewText(parameter.evidence)}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <FdaReviewEvidenceLink url={parameter.source_url} page={parameter.page} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {topicRows.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-xs font-semibold text-foreground">影响试验设计的临床药理结论</h4>
          <div className="divide-y divide-border rounded-md border border-border">
            {topicRows.map((topic, index) => (
              <div key={`${topic.label}-${String(topic.page || index)}-${index}`} className="grid gap-2 px-3 py-2.5 text-xs sm:grid-cols-[180px_1fr_auto]">
                <strong className="text-foreground">{topic.label}</strong>
                <span className="leading-relaxed text-muted-foreground">{fdaReviewText(topic.summary)}</span>
                <span className="sm:text-right">
                  <FdaReviewEvidenceLink url={topic.source_url} page={topic.page} />
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(gaps.length > 0 || managerNotes.length > 0) && (
        <div className="mt-4 grid gap-4 border-t border-border pt-3 md:grid-cols-2">
          {gaps.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-300">证据缺口</h4>
              <ul className="mt-2 space-y-1 text-xs leading-relaxed text-muted-foreground">
                {gaps.map((gap) => <li key={gap}>• {gap}</li>)}
              </ul>
            </div>
          )}
          {managerNotes.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-foreground">项目经理复核要点</h4>
              <ul className="mt-2 space-y-1 text-xs leading-relaxed text-muted-foreground">
                {managerNotes.map((note) => <li key={note}>• {note}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function FdaReviewDocuments({ review }: { review: Record<string, unknown> }) {
  const documents = fdaReviewDocumentsForDisplay(review);
  const applicationSummaries = fdaReviewApplicationSummaries(review, documents);
  const clinicalPharmCount = documents.filter((document) =>
    fdaReviewEnglishCategory(document).toLowerCase().includes("clinical pharmacology"),
  ).length;
  const professionalNote = fdaReviewText(review.professional_note, "");
  const clinicalPkEvidence = fdaReviewRecord(review.clinical_pk_evidence);

  if (applicationSummaries.length === 0 && documents.length === 0 && Object.keys(clinicalPkEvidence).length === 0) return null;

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-md border border-border bg-muted/15 p-3">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs">
          <span>
            <span className="text-muted-foreground">FDA 申请 </span>
            <strong className="text-foreground">{String(review.application_count ?? applicationSummaries.length)}</strong>
          </span>
          <span>
            <span className="text-muted-foreground">关键 PDF </span>
            <strong className="text-foreground">{documents.length}</strong>
          </span>
          <span>
            <span className="text-muted-foreground">临床药理/BE </span>
            <strong className="text-foreground">{clinicalPharmCount}</strong>
          </span>
        </div>
        {professionalNote && (
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            {professionalNote}
          </p>
        )}
      </div>

      {Object.keys(clinicalPkEvidence).length > 0 && (
        <FdaReviewPkEvidence evidence={clinicalPkEvidence} />
      )}

      {applicationSummaries.length > 0 && (
        <div>
          <div className="mb-2 text-xs font-semibold text-foreground">申请概览</div>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-[760px] w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">FDA 申请号</th>
                  <th className="px-3 py-2 text-left font-medium">商品名</th>
                  <th className="px-3 py-2 text-left font-medium">活性成分</th>
                  <th className="px-3 py-2 text-left font-medium">申请人</th>
                  <th className="px-3 py-2 text-center font-medium">关键 PDF</th>
                  <th className="px-3 py-2 text-right font-medium">申请页</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {applicationSummaries.map((application) => {
                  const applicationNumber = fdaReviewText(application.application_number);
                  const overviewUrl = String(application.overview_url || "");
                  return (
                    <tr key={`${applicationNumber}-${overviewUrl}`} className="hover:bg-muted/20">
                      <td className="px-3 py-2.5 font-mono font-medium text-foreground">{applicationNumber}</td>
                      <td className="px-3 py-2.5 text-foreground">{fdaReviewText(application.brand_names)}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{fdaReviewText(application.active_ingredients)}</td>
                      <td className="px-3 py-2.5 text-muted-foreground">{fdaReviewText(application.sponsor_name)}</td>
                      <td className="px-3 py-2.5 text-center">
                        <span className="rounded bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                          {String(application.key_document_count ?? 0)}
                        </span>
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        {overviewUrl ? (
                          <a
                            href={overviewUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-primary hover:underline"
                          >
                            查看申请页
                            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                          </a>
                        ) : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <div className="mb-2 text-xs font-semibold text-foreground">关键审评 PDF</div>
        {documents.length > 0 ? (
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-[920px] w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">序号</th>
                  <th className="px-3 py-2 text-left font-medium">FDA 申请号</th>
                  <th className="px-3 py-2 text-left font-medium">文档类型</th>
                  <th className="px-3 py-2 text-left font-medium">临床设计价值</th>
                  <th className="px-3 py-2 text-left font-medium">日期</th>
                  <th className="px-3 py-2 text-right font-medium">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {documents.map((document, index) => {
                  const url = String(document.url || "");
                  const isPdf = Boolean(document.is_pdf);
                  const category = fdaReviewCategoryLabel(document);
                  const englishCategory = fdaReviewEnglishCategory(document);
                  const domain = fdaReviewDomain(document);
                  const displayName = fdaReviewText(
                    document.display_name || document.file_name || document.title || document.document_type,
                    "FDA Review",
                  );
                  const downloadName = isPdf
                    ? String(document.file_name || document.display_name || "")
                    : "";
                  return (
                    <tr key={`${url}-${index}`} className="hover:bg-muted/20">
                      <td className="px-3 py-2.5 text-muted-foreground">{index + 1}</td>
                      <td className="px-3 py-2.5 font-mono text-foreground">
                        {fdaReviewText(document.application_number)}
                      </td>
                      <td className="px-3 py-2.5">
                        <div className="font-medium text-foreground">{category}</div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-primary">{domain}</span>
                          <span>{englishCategory}</span>
                        </div>
                        <div className="mt-1 max-w-[320px] truncate text-[11px] text-muted-foreground" title={displayName}>
                          {displayName}
                        </div>
                      </td>
                      <td className="px-3 py-2.5 max-w-[360px] leading-relaxed text-muted-foreground">
                        {fdaReviewClinicalValue(document)}
                      </td>
                      <td className="px-3 py-2.5 whitespace-nowrap text-muted-foreground">
                        {formatFdaDate(document.date || document.submission_status_date)}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        {url ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download={downloadName || undefined}
                            className="inline-flex items-center gap-1.5 text-primary hover:underline"
                          >
                            <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                            {isPdf ? "打开 PDF" : "打开目录"}
                          </a>
                        ) : "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="rounded-md border border-border bg-muted/15 p-3 text-xs text-muted-foreground">
            FDA 当前未公开该申请的 Review 文件链接。建议查看 Drugs@FDA 申请页及官方说明书，并记录人工复核结论。
          </p>
        )}
      </div>
    </div>
  );
}

function MarkdownTableRenderer({ content }: { content: string }) {
  if (!content || !content.includes("|") || !content.includes("---")) {
    return <pre className="mt-2 p-3 bg-muted/30 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all">{content}</pre>;
  }
  const lines = content.trim().split("\n").filter(l => l.trim());
  if (lines.length < 2) return <pre className="mt-2 p-3 bg-muted/30 rounded text-xs overflow-x-auto whitespace-pre-wrap">{content}</pre>;
  const headerLine = lines[0];
  const headers = parseMarkdownRow(headerLine);
  const dataLines = lines.slice(2).filter((line) => line.includes("|"));
  const rows = dataLines.map((line) =>
    alignMarkdownRow(parseMarkdownRow(line), headers.length),
  );
  return (
    <div className="mt-3 overflow-x-auto rounded-lg border border-border">
      <table className="min-w-full text-xs">
        <thead>
          <tr className="bg-muted/50">
            {headers.map((h, i) => (
              <th key={i} className="px-3 py-2 text-left font-medium text-foreground whitespace-nowrap border-b border-border">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-muted/20 transition-colors">
              {row.map((cell, ci) => (
                <td key={ci} className="px-3 py-2 text-muted-foreground whitespace-nowrap max-w-[200px] truncate" title={cell}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CdeReferenceSummary({ data }: { data: Record<string, unknown> }) {
  const referenceDrugs = Array.isArray(data.reference_drugs)
    ? data.reference_drugs as Record<string, unknown>[]
    : [];
  const markdownTable = typeof data.markdown_table === "string"
    ? data.markdown_table
    : "";

  return (
    <div className="mt-3">
      {referenceDrugs.length > 0 ? (
        <ReferenceDrugTable drugs={referenceDrugs} />
      ) : markdownTable && markdownTable.includes("|") ? (
        <MarkdownTableRenderer content={markdownTable} />
      ) : (
        <p className="text-sm text-muted-foreground">
          No displayable reference drug records.
        </p>
      )}
    </div>
  );
}

function DataRow({ label, value, highlight }: { label: string; value?: string; highlight?: boolean }) {
  if (!value) return null;
  if (value.includes("|") && value.includes("---") && value.includes("\n")) {
    return (
      <div className="data-row group">
        <span className="data-label">{label}</span>
        <MarkdownTableRenderer content={value} />
      </div>
    );
  }
  return (
    <div className="data-row group">
      <span className="data-label">{label}</span>
      <span className={`data-value ${highlight ? "text-primary font-medium" : ""}`}>
        {value}
      </span>
    </div>
  );
}

function LinkChip({ href, children }: { href: string; children: React.ReactNode }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="link mt-3"
    >
      {children}
      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  );
}

function uniqueJoined(values: unknown[]): string {
  return Array.from(
    new Set(values.map((value) => String(value || "").trim()).filter(Boolean)),
  ).join(" / ");
}

function getFdaStrengths(data: Record<string, unknown>): string {
  const explicitValues: string[] = [];
  appendDelimitedSummaryValues(explicitValues, data.available_strengths_list);
  appendDelimitedSummaryValues(explicitValues, data.available_strengths);
  const explicitStrengths = joinStrengthValues(explicitValues, 32);
  if (explicitStrengths) return explicitStrengths;

  const collectedStrengths = joinStrengthValues(
    collectFdaStrengthValues(data),
    32,
  );
  if (collectedStrengths) return collectedStrengths;

  const products = Array.isArray(data.approval_products)
    ? data.approval_products as Record<string, unknown>[]
    : [];
  const selectedForm = String(data.dosage_form || "").toUpperCase();
  const matchingProducts = products.filter(
    (product) =>
      !selectedForm ||
      String(product.dosage_form || "").toUpperCase() === selectedForm,
  );

  return joinStrengthValues(
    (matchingProducts.length > 0 ? matchingProducts : products).map((product) => {
      const strength = String(product.strength || "").trim();
      const status = String(product.marketing_status || "").toUpperCase();
      return status.includes("DISCONTINUED") && strength
        ? `${strength}（已停产）`
        : strength;
    }),
    32,
  );
}

function getFdaApplicationNumbers(data: Record<string, unknown>): string {
  const values: string[] = [];
  appendFdaApplicationNumbers(values, data.original_application_numbers);
  appendFdaApplicationNumbers(values, data.all_application_numbers);
  appendFdaApplicationNumbers(values, data.application_number);

  const review = data.fda_review as Record<string, unknown> | undefined;
  if (review) {
    recordArray(review.applications).forEach((app) => {
      appendFdaApplicationNumbers(values, app.application_number);
      appendFdaApplicationNumbers(values, app.appl_no);
    });
  }

  const unique = uniqueNormalizedValues(
    values.map((value) => normalizeFdaApplicationNumber(value)).filter(Boolean),
  );
  if (unique.length === 0) return "";
  const visible = unique.slice(0, 12);
  return unique.length > visible.length
    ? `${visible.join(SUMMARY_LIST_SEPARATOR)} 等 ${unique.length} 个申请号`
    : visible.join(SUMMARY_LIST_SEPARATOR);
}

function truncateClinicalText(value: unknown, maxLength = 260): string {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text) return "";
  return text.length > maxLength ? `${text.slice(0, maxLength).trim()}...` : text;
}

interface PkMetric {
  parameter: string;
  notation?: string;
  value: string;
  unit?: string;
  qualifier?: string;
  context?: string;
  source_text?: string;
}

const PK_PARAMETER_LABELS: Record<string, string> = {
  Cmax: "峰浓度",
  AUC: "药时曲线下面积",
  tmax: "达峰时间",
  "t1/2": "消除半衰期",
  bioavailability: "绝对生物利用度",
  protein_binding: "血浆蛋白结合率",
  clearance: "清除率",
  Vd: "表观分布容积",
  steady_state: "达稳态时间",
  accumulation_ratio: "蓄积比",
};

function PkNotation({ metric }: { metric: PkMetric }) {
  switch (metric.parameter) {
    case "Cmax":
      return <><i>C</i><sub>max</sub></>;
    case "AUC": {
      const suffix = String(metric.notation || "")
        .replace(/^AUC/i, "")
        .replace(/[()]/g, "")
        .replace(/-/g, "–")
        .trim();
      return <><i>AUC</i>{suffix && <sub>{suffix}</sub>}</>;
    }
    case "tmax":
      return <><i>T</i><sub>max</sub></>;
    case "t1/2":
      return <><i>t</i><sub>1/2</sub></>;
    case "bioavailability":
      return <i>F</i>;
    case "protein_binding":
      return <i>PPB</i>;
    case "clearance":
      return <i>CL</i>;
    case "Vd":
      return <><i>V</i><sub>d</sub></>;
    case "steady_state":
      return <><i>t</i><sub>ss</sub></>;
    case "accumulation_ratio":
      return <><i>R</i><sub>acc</sub></>;
    default:
      return <i>{metric.notation || metric.parameter}</i>;
  }
}

function getCompactPk(data: Record<string, unknown>): string {
  if (!data.pk_parsed || typeof data.pk_parsed !== "object" || Array.isArray(data.pk_parsed)) {
    return "";
  }

  const pk = data.pk_parsed as Record<string, unknown>;
  const labels: Record<string, string> = {
    Cmax: "Cmax",
    AUC: "AUC",
    tmax: "Tmax",
    "t1/2": "T1/2",
    bioavailability: "生物利用度",
    protein_binding: "蛋白结合率",
    clearance: "清除率",
    Vd: "分布容积",
  };

  return Object.entries(labels)
    .map(([key, label]) => {
      const value = String(pk[key] || "").trim();
      return value ? `${label}: ${value}` : "";
    })
    .filter(Boolean)
    .join(" · ");
}

function safePkSummaryValue(value: unknown): string {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  if (!text || text === "—" || text === "-") return "";
  if (text.length > 160) return "";
  if (
    /(pharmacokinetics|clinical pharmacology|after a single|following oral|section\s*\d+|^\d+\.\d+\s+[A-Z][a-z]+)/i.test(text) &&
    text.length > 35
  ) {
    return "";
  }
  return text;
}

function formatPkMetricForSummary(metric: PkMetric): string {
  const value = safePkSummaryValue(metric.value);
  if (!value) return "";
  const qualifier = String(metric.qualifier || "").trim();
  const unit = String(metric.unit || "").trim();
  const prefix = qualifier ? `${qualifier} ` : "";
  const hasUnit = unit && value.toLowerCase().includes(unit.toLowerCase());
  return `${prefix}${value}${unit && !hasUnit ? ` ${unit}` : ""}`.trim();
}

function getSummaryPkMetric(metrics: PkMetric[], parameter: string): string {
  const values = metrics
    .filter((metric) => metric.parameter === parameter)
    .map((metric) => {
      const value = formatPkMetricForSummary(metric);
      if (!value) return "";
      const context = String(metric.context || "").trim();
      return context ? `${context}: ${value}` : value;
    })
    .filter(Boolean);

  return Array.from(new Set(values)).slice(0, 3).join("; ");
}

function buildSummaryPkParams(data?: Record<string, unknown>): SummaryPreviewCardProps["pkParams"] {
  const empty: SummaryPreviewCardProps["pkParams"] = {
    t12: "",
    bioavailability: "",
  };
  if (!data) return empty;

  const pkParsed =
    data.pk_parsed && typeof data.pk_parsed === "object" && !Array.isArray(data.pk_parsed)
      ? data.pk_parsed as Record<string, unknown>
      : {};
  const metrics = Array.isArray(data.pk_metrics)
    ? (data.pk_metrics as PkMetric[]).filter((metric) => metric && metric.parameter && metric.value)
    : [];
  const valueFrom = (parameter: string, parsedKey = parameter) =>
    getSummaryPkMetric(metrics, parameter) || safePkSummaryValue(pkParsed[parsedKey]);

  const summary: SummaryPreviewCardProps["pkParams"] = {
    cmax: valueFrom("Cmax"),
    auc: valueFrom("AUC"),
    tmax: valueFrom("tmax"),
    t12: valueFrom("t1/2"),
    bioavailability: valueFrom("bioavailability"),
    vd: valueFrom("Vd"),
    cl: valueFrom("clearance"),
    proteinBinding: valueFrom("protein_binding"),
  };

  const hasAnyValue = Object.entries(summary).some(
    ([key, value]) => key !== "notes" && Boolean(value),
  );
  const rawPkText = String(data.pk_parameters || "").trim();
  if (!hasAnyValue && rawPkText.length > 0) {
    summary.notes = "已获取 FDA 药代动力学原文，但未自动提取出可统计的关键数值；请在 FDA 数据源中查看完整原文。";
  }

  return summary;
}

type SummaryIdentityItem = NonNullable<SummaryPreviewCardProps["identityItems"]>[number];
type SummaryApprovalRow = NonNullable<SummaryPreviewCardProps["approvalRows"]>[number];
type SummaryEvidenceLink = NonNullable<SummaryPreviewCardProps["evidenceLinks"]>[number];

function summaryText(value: unknown): string {
  if (Array.isArray(value)) return uniqueJoined(value);
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  if (!text || text === "—" || text === "-" || text === "null" || text === "undefined") {
    return "";
  }
  return text;
}

function firstSummaryValue(...values: unknown[]): string {
  for (const value of values) {
    const text = summaryText(value);
    if (text) return text;
  }
  return "";
}

function firstSummaryRecord(value: unknown): Record<string, unknown> {
  return recordArray(value)[0] || {};
}

const SUMMARY_LIST_SEPARATOR = "、";
const SUMMARY_STRENGTH_LIMIT = 16;

function normalizedSummaryScalar(value: unknown): string {
  const text = String(value ?? "").replace(/\s+/g, " ").trim();
  if (!text || text === "—" || text === "-" || text === "null" || text === "undefined") {
    return "";
  }
  return text;
}

function appendDelimitedSummaryValues(target: string[], value: unknown) {
  if (Array.isArray(value)) {
    value.forEach((item) => appendDelimitedSummaryValues(target, item));
    return;
  }
  const text = normalizedSummaryScalar(value);
  if (!text || text === "[object Object]") return;

  text
    .split(/[、,，;；]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .forEach((item) => target.push(item));
}

function normalizeStrengthUnit(unit: string): string {
  const normalized = unit.toUpperCase();
  if (normalized === "MG") return "mg";
  if (normalized === "MCG" || normalized === "UG" || normalized === "ΜG" || normalized === "μG") return "mcg";
  if (normalized === "G") return "g";
  if (normalized === "ML") return "mL";
  if (normalized === "L") return "L";
  if (normalized === "MEQ") return "mEq";
  if (normalized === "IU") return "IU";
  if (normalized === "U") return "U";
  if (normalized === "UNIT") return "unit";
  if (normalized === "UNITS") return "units";
  return unit;
}

function formatStrengthText(value: string): string {
  return value
    .replace(/\s+/g, " ")
    .replace(/(\d+(?:[.,]\d+)?)\s*(MG|MCG|UG|ΜG|μG|G)\s*\/\s*(ML|L)\b/gi, (_match, amount, unit, denominator) => (
      `${String(amount).replace(",", ".")} ${normalizeStrengthUnit(unit)}/${normalizeStrengthUnit(denominator)}`
    ))
    .replace(/(\d+(?:[.,]\d+)?)\s*(MG|MCG|UG|ΜG|μG|G|ML|L|MEQ|IU|U|UNIT|UNITS)\b/gi, (_match, amount, unit) => (
      `${String(amount).replace(",", ".")} ${normalizeStrengthUnit(unit)}`
    ))
    .replace(/\s*\/\s*/g, " / ")
    .replace(/\s+/g, " ")
    .trim();
}

function strengthSortValue(value: string): number {
  const match = value.match(/\d+(?:\.\d+)?/);
  return match ? Number(match[0]) : Number.MAX_SAFE_INTEGER;
}

function uniqueNormalizedValues(values: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  values.forEach((value) => {
    const text = normalizedSummaryScalar(value);
    if (!text) return;
    const key = text.toLocaleLowerCase().replace(/\s+/g, "");
    if (seen.has(key)) return;
    seen.add(key);
    unique.push(text);
  });
  return unique;
}

function joinStrengthValues(values: string[], limit = SUMMARY_STRENGTH_LIMIT): string {
  const formatted = values
    .map(formatStrengthText)
    .filter(Boolean)
    .sort((left, right) => {
      const leftNumber = strengthSortValue(left);
      const rightNumber = strengthSortValue(right);
      if (leftNumber !== rightNumber) return leftNumber - rightNumber;
      return left.localeCompare(right, "en", { numeric: true, sensitivity: "base" });
    });
  const unique = uniqueNormalizedValues(formatted);
  const visible = unique.slice(0, limit);
  return unique.length > limit
    ? `${visible.join(SUMMARY_LIST_SEPARATOR)} 等 ${unique.length} 个规格`
    : visible.join(SUMMARY_LIST_SEPARATOR);
}

function normalizeDosageFormForCompare(value: unknown): string {
  return normalizedSummaryScalar(value)
    .toLocaleUpperCase()
    .replace(/\s+/g, "")
    .replace(/[剂型剤]/g, "")
    .replace(/TABLETS?/g, "TABLET")
    .replace(/CAPSULES?/g, "CAPSULE");
}

function isSameDosageForm(left: unknown, right: unknown): boolean {
  const leftText = normalizeDosageFormForCompare(left);
  const rightText = normalizeDosageFormForCompare(right);
  return Boolean(leftText && rightText && (leftText === rightText || leftText.includes(rightText) || rightText.includes(leftText)));
}

function activeIngredientStrength(product: Record<string, unknown>): string {
  const ingredients = recordArray(product.active_ingredients)
    .map((ingredient) => formatStrengthText(summaryText(ingredient.strength)))
    .filter(Boolean);
  return uniqueNormalizedValues(ingredients).join(" / ");
}

function collectFdaStrengthValues(fda: Record<string, unknown>, selectedDosageForm?: DosageForm | null): string[] {
  const products = recordArray(fda.approval_products);
  const selectedForm = firstSummaryValue(fda.dosage_form, selectedDosageForm?.name);
  const matchingProducts = products.filter((product) => !selectedForm || isSameDosageForm(product.dosage_form, selectedForm));
  const sourceProducts = matchingProducts.length > 0 ? matchingProducts : products;
  const values: string[] = [];

  sourceProducts.forEach((product) => {
    appendDelimitedSummaryValues(values, product.strength);
    appendDelimitedSummaryValues(values, product.approval_strength);
    appendDelimitedSummaryValues(values, activeIngredientStrength(product));
  });

  appendDelimitedSummaryValues(values, fda.approval_strength);
  appendDelimitedSummaryValues(values, fda.strength);
  return values;
}

function collectProductStrengthValues(products: unknown, fields: string[]): string[] {
  const values: string[] = [];
  recordArray(products).forEach((product) => {
    fields.forEach((field) => appendDelimitedSummaryValues(values, product[field]));
    appendDelimitedSummaryValues(values, product.strengths);
    recordArray(product.presentations).forEach((presentation) => {
      fields.forEach((field) => appendDelimitedSummaryValues(values, presentation[field]));
      appendDelimitedSummaryValues(values, presentation.strengths);
    });
  });
  return values;
}

function buildSummarySpecifications(
  data: Record<string, unknown>,
  specification: string,
  selectedDosageForm?: DosageForm | null,
): string {
  const fda = (data.fda || {}) as Record<string, unknown>;
  const ema = (data.ema || {}) as Record<string, unknown>;
  const pmda = (data.pmda || {}) as Record<string, unknown>;
  const cdeRef = (data.cde_reflisted || {}) as Record<string, unknown>;
  const dailyMed = (data.dailymed || {}) as Record<string, unknown>;

  const structuredValues = [
    ...collectFdaStrengthValues(fda, selectedDosageForm),
    ...collectProductStrengthValues(ema.products, ["strength", "specification", "presentation_strength"]),
    ...collectProductStrengthValues(pmda.products, ["strength", "specification", "package_strength", "approval_strength"]),
    ...collectProductStrengthValues(dailyMed.products, ["strength", "specification"]),
  ];
  appendDelimitedSummaryValues(structuredValues, dailyMed.strength);

  const cdeValues = collectProductStrengthValues(cdeRef.reference_drugs, ["strength", "specification"]);
  const fallbackValues: string[] = [];
  appendDelimitedSummaryValues(fallbackValues, specification);

  return joinStrengthValues([...structuredValues, ...cdeValues, ...fallbackValues]);
}

function normalizeFdaApplicationNumber(value: string): string {
  const match = value.match(/\b(NDA|BLA|ANDA)\s*0*([0-9]{3,})\b/i);
  if (!match) return value.toUpperCase().replace(/\s+/g, "");
  return `${match[1].toUpperCase()}${match[2]}`;
}

function appendFdaApplicationNumbers(target: string[], value: unknown) {
  if (Array.isArray(value)) {
    value.forEach((item) => appendFdaApplicationNumbers(target, item));
    return;
  }
  const text = normalizedSummaryScalar(value);
  if (!text || text === "[object Object]") return;
  const matches = Array.from(text.matchAll(/\b(NDA|BLA|ANDA)\s*0*[0-9]{3,}\b/gi));
  if (matches.length > 0) {
    matches.forEach((match) => target.push(normalizeFdaApplicationNumber(match[0])));
  } else if (/^(NDA|BLA|ANDA)?\s*[0-9]{3,}$/i.test(text)) {
    target.push(normalizeFdaApplicationNumber(text));
  }
}

function collectApplicationNumbersFromRecord(target: string[], record: Record<string, unknown>, depth = 0) {
  [
    "application_number",
    "application_no",
    "applicationNumber",
    "application",
    "app_no",
    "nda",
    "nda_number",
    "bla_number",
    "anda_number",
  ].forEach((field) => appendFdaApplicationNumbers(target, record[field]));

  if (depth >= 2) return;
  [
    "applications",
    "approval_products",
    "review_documents",
    "documents",
    "target_review_pdfs",
    "reviews",
    "pdfs",
  ].forEach((field) => {
    recordArray(record[field]).forEach((child) => collectApplicationNumbersFromRecord(target, child, depth + 1));
  });
}

function buildSummaryFdaApplications(data: Record<string, unknown>): string {
  const values: string[] = [];
  collectApplicationNumbersFromRecord(values, (data.fda || {}) as Record<string, unknown>);
  collectApplicationNumbersFromRecord(values, (data.fda_review || {}) as Record<string, unknown>);

  const unique = uniqueNormalizedValues(values.map(normalizeFdaApplicationNumber));
  const originals = unique.filter((application) => /^(NDA|BLA)/.test(application));
  const chosen = originals.length > 0 ? originals : unique;
  return chosen
    .sort((left, right) => left.localeCompare(right, "en", { numeric: true, sensitivity: "base" }))
    .join(SUMMARY_LIST_SEPARATOR);
}

function buildSummaryDrugName(
  result: NonNullable<TaskStatus["result"]>,
  selectedDrug: DrugMapping | null,
): SummaryPreviewCardProps["drugName"] {
  const data = result.data || {};
  const fda = (data.fda || {}) as Record<string, unknown>;
  const emaProduct = firstSummaryRecord((data.ema as Record<string, unknown> | undefined)?.products);
  const pubchem = (data.pubchem || {}) as Record<string, unknown>;
  const firstCompound = firstSummaryRecord(pubchem.compounds);

  const cn = firstSummaryValue(selectedDrug?.nameZh, result.drug_name);
  const en = firstSummaryValue(
    selectedDrug?.nameEn,
    selectedDrug?.inn,
    fda.generic_name,
    emaProduct.active_substance,
    emaProduct.generic_name,
    pubchem.normalized_query,
    firstCompound.title,
    result.drug_name,
  );
  const inn = firstSummaryValue(selectedDrug?.inn, fda.generic_name, emaProduct.active_substance, en);

  return { cn, en, inn };
}

function buildSummaryComponents(
  pubchem: Record<string, unknown> | undefined,
  selectedDrug: DrugMapping | null,
): Array<{ cn: string; en: string }> {
  const compounds = recordArray(pubchem?.compounds)
    .map((compound) => ({
      cn: firstSummaryValue(compound.query_component_cn, compound.query_component, compound.name_zh),
      en: firstSummaryValue(compound.title, compound.name, compound.query_component, compound.iupac_name),
    }))
    .filter((compound) => compound.cn || compound.en)
    .slice(0, 4);

  if (compounds.length > 0) return compounds;
  if (!selectedDrug || !/[+/]| and /i.test(selectedDrug.nameEn)) return [];
  return [{ cn: selectedDrug.nameZh, en: selectedDrug.nameEn }];
}

function buildSummaryIdentityItems(
  result: NonNullable<TaskStatus["result"]>,
  selectedDrug: DrugMapping | null,
  selectedDosageForm: DosageForm | null,
  specification: string,
): SummaryIdentityItem[] {
  const data = result.data || {};
  const fda = (data.fda || {}) as Record<string, unknown>;
  const emaProduct = firstSummaryRecord((data.ema as Record<string, unknown> | undefined)?.products);
  const pubchem = (data.pubchem || {}) as Record<string, unknown>;
  const firstCompound = firstSummaryRecord(pubchem.compounds);
  const formula = firstSummaryValue(
    pubchem.molecular_formula,
    firstCompound.molecular_formula,
    firstCompound.formula,
  );
  const cid = firstSummaryValue(pubchem.cid, firstCompound.cid, firstCompound.pubchem_cid);
  const summarizedSpecifications = buildSummarySpecifications(data, specification, selectedDosageForm);
  const summarizedApplications = buildSummaryFdaApplications(data);

  return [
    { label: "中文检索名", value: firstSummaryValue(selectedDrug?.nameZh, result.drug_name), accent: true },
    {
      label: "英文 / INN",
      value: firstSummaryValue(
        selectedDrug?.nameEn,
        selectedDrug?.inn,
        fda.generic_name,
        emaProduct.active_substance,
        pubchem.normalized_query,
      ),
      accent: true,
    },
    { label: "剂型", value: firstSummaryValue(fda.dosage_form, selectedDosageForm?.name) },
    {
      label: "规格",
      value: summarizedSpecifications,
    },
    { label: "FDA 申请号", value: summarizedApplications || firstSummaryValue(fda.application_number, fda.application_no) },
    { label: "分子式", value: formula, format: "formula" },
    { label: "PubChem CID", value: cid },
    { label: "查询任务", value: result.task_id, hint: "用于追踪本次查询与报告下载" },
  ];
}

export function buildSummaryApprovalRows(data?: Record<string, unknown>): SummaryApprovalRow[] {
  if (!data) return [];
  const rows: SummaryApprovalRow[] = [];
  const fda = (data.fda || {}) as Record<string, unknown>;
  const emaProduct = firstSummaryRecord((data.ema as Record<string, unknown> | undefined)?.products);
  const pmdaProduct = firstSummaryRecord((data.pmda as Record<string, unknown> | undefined)?.products);
  const cdeRef = firstSummaryRecord((data.cde_reflisted as Record<string, unknown> | undefined)?.reference_drugs);
  const healthCanada = (data.health_canada || {}) as Record<string, unknown>;
  const healthCanadaProducts = recordArray(healthCanada.products);
  const healthCanadaProduct =
    healthCanadaProducts.find((product) =>
      String(product.product_type_code || "") === "originator_candidate" &&
      String(product.status_category || "").toLowerCase() === "marketed"
    ) ||
    healthCanadaProducts.find((product) =>
      String(product.status_category || "").toLowerCase() === "marketed"
    ) ||
    healthCanadaProducts[0] ||
    {};
  const dailyMed = (data.dailymed || {}) as Record<string, unknown>;

  if (firstSummaryValue(fda.brand_name, fda.generic_name)) {
    rows.push({
      region: "US",
      agency: "FDA",
      product: firstSummaryValue(fda.brand_name, fda.openfda_brand_name, fda.generic_name),
      ingredient: firstSummaryValue(fda.generic_name, fda.active_ingredients),
      strength: joinStrengthValues(collectFdaStrengthValues(fda)) || firstSummaryValue(fda.approval_strength, fda.strength, getFdaStrengths(fda)),
      holder: firstSummaryValue(fda.manufacturer, fda.sponsor_name),
      status: firstSummaryValue(fda.marketing_status, fda.status, "Label"),
      href: resolveDocumentHref(getSourceUrl("fda", fda)),
      linkLabel: "FDA Label",
    });
  }

  if (firstSummaryValue(emaProduct.product_name, emaProduct.name, emaProduct.brand_name)) {
    rows.push({
      region: "EU",
      agency: "EMA",
      product: firstSummaryValue(emaProduct.product_name, emaProduct.name, emaProduct.brand_name),
      ingredient: firstSummaryValue(emaProduct.active_substance, emaProduct.generic_name),
      strength: firstSummaryValue(emaProduct.strength, emaProduct.specification),
      holder: firstSummaryValue(emaProduct.marketing_authorisation_holder, emaProduct.holder),
      status: firstSummaryValue(emaProduct.status, emaProduct.medicine_status),
      date: firstSummaryValue(emaProduct.authorization_date, emaProduct.authorisation_date),
      href: resolveDocumentHref(firstSummaryValue(emaProduct.epar_url, emaProduct.url, emaProduct.page_url)),
      linkLabel: "EMA EPAR",
    });
  }

  if (firstSummaryValue(pmdaProduct.product_name, pmdaProduct.title, pmdaProduct.name)) {
    rows.push({
      region: "JP",
      agency: "PMDA",
      product: firstSummaryValue(pmdaProduct.product_name, pmdaProduct.title, pmdaProduct.name),
      ingredient: firstSummaryValue(pmdaProduct.name, pmdaProduct.active_ingredient),
      strength: firstSummaryValue(pmdaProduct.strength, pmdaProduct.specification),
      holder: firstSummaryValue(pmdaProduct.holder, pmdaProduct.company, pmdaProduct.manufacturer),
      status: firstSummaryValue(pmdaProduct.status, "PMDA"),
      href: resolveDocumentHref(firstSummaryValue(pmdaProduct.url, pmdaProduct.page_url)),
      linkLabel: "PMDA",
    });
  }

  if (firstSummaryValue(cdeRef.drug_name, cdeRef.product_name)) {
    rows.push({
      region: "CN",
      agency: "CDE 参比制剂",
      product: firstSummaryValue(cdeRef.drug_name, cdeRef.product_name),
      ingredient: firstSummaryValue(cdeRef.english_name, cdeRef.generic_name),
      strength: firstSummaryValue(cdeRef.specification, cdeRef.strength),
      holder: firstSummaryValue(cdeRef.holder, cdeRef.company, cdeRef.manufacturer),
      status: firstSummaryValue(cdeRef.approval_status, cdeRef.status, "参考"),
      href: resolveDocumentHref(getSourceUrl("cde_reflisted", data.cde_reflisted as Record<string, unknown>)),
      linkLabel: "CDE",
    });
  }

  if (firstSummaryValue(healthCanadaProduct.brand_name, healthCanadaProduct.descriptor)) {
    const healthCanadaLinks = recordArray(healthCanadaProduct.document_links);
    const monographLink = healthCanadaLinks.find((link) =>
      String(link.kind || "") === "product_monograph"
    );
    const dpdLink = healthCanadaLinks.find((link) =>
      String(link.kind || "") === "dpd_product" ||
      String(link.url || "").includes("dpd-bdpp")
    );
    const href = firstSummaryValue(
      healthCanadaProduct.product_monograph_url,
      monographLink?.url,
      healthCanadaProduct.source_url,
      dpdLink?.url,
    );

    rows.push({
      region: "CA",
      agency: "Health Canada",
      product: firstSummaryValue(healthCanadaProduct.brand_name, healthCanadaProduct.descriptor),
      ingredient: firstSummaryValue(
        healthCanadaProduct.active_ingredient_names,
        healthCanadaProduct.active_ingredients,
      ),
      strength: firstSummaryValue(healthCanadaProduct.specification, healthCanadaProduct.strength),
      holder: firstSummaryValue(healthCanadaProduct.company, healthCanadaProduct.company_name),
      status: firstSummaryValue(healthCanadaProduct.status, healthCanadaProduct.status_category),
      date: firstSummaryValue(
        healthCanadaProduct.original_market_date,
        healthCanadaProduct.last_update_date,
      ),
      href: resolveDocumentHref(href),
      linkLabel: healthCanadaProduct.product_monograph_url || monographLink?.url
        ? "Product Monograph"
        : "Health Canada DPD",
    });
  }

  if (firstSummaryValue(dailyMed.drug_name, dailyMed.title, dailyMed.set_id)) {
    rows.push({
      region: "US",
      agency: "DailyMed",
      product: firstSummaryValue(dailyMed.drug_name, dailyMed.title),
      ingredient: firstSummaryValue(dailyMed.active_ingredient, dailyMed.generic_name),
      strength: firstSummaryValue(dailyMed.strength, dailyMed.dosage_form),
      holder: firstSummaryValue(dailyMed.labeler, dailyMed.manufacturer),
      status: firstSummaryValue(dailyMed.status, "Label"),
      href: resolveDocumentHref(firstSummaryValue(dailyMed.label_url, dailyMed.url, dailyMed.source_url)),
      linkLabel: "Label",
    });
  }

  return rows.filter((row) => row.product || row.ingredient);
}

export function buildSummaryEvidenceLinks(data?: Record<string, unknown>): SummaryEvidenceLink[] {
  if (!data) return [];
  const links: SummaryEvidenceLink[] = [];
  const seen = new Set<string>();
  const add = (
    source: string,
    type: string,
    title: unknown,
    href: unknown,
    date?: unknown,
    note?: unknown,
    priority: SummaryEvidenceLink["priority"] = "standard",
  ) => {
    const cleanTitle = summaryText(title);
    const cleanHref = resolveDocumentHref(summaryText(href));
    if (!cleanTitle && !cleanHref) return;
    const key = `${source}|${type}|${cleanTitle}|${cleanHref}`;
    if (seen.has(key)) return;
    seen.add(key);
    links.push({
      source,
      type,
      title: cleanTitle || cleanHref,
      href: cleanHref || undefined,
      date: summaryText(date) || undefined,
      note: summaryText(note) || undefined,
      priority,
    });
  };

  const fdaReview = (data.fda_review || {}) as Record<string, unknown>;
  const reviewDocs = [
    ...recordArray(fdaReview.target_review_pdfs),
    ...recordArray(fdaReview.review_documents),
    ...recordArray(fdaReview.documents),
  ];
  reviewDocs
    .filter((doc) => /pdf/i.test(firstSummaryValue(doc.url, doc.href, doc.link, doc.file_url, doc.document_url)))
    .slice(0, 8)
    .forEach((doc) => {
      const title = firstSummaryValue(doc.display_name, doc.file_name, doc.title, doc.label, doc.document_name);
      const highPriority = /clinical pharmacology|biopharmaceutics|clinical review|product quality|printed labeling|approval letter/i.test(title);
      add(
        "FDA Review",
        firstSummaryValue(doc.review_type, doc.type, doc.document_type, "Review PDF"),
        title,
        firstSummaryValue(doc.url, doc.href, doc.link, doc.file_url, doc.document_url),
        firstSummaryValue(doc.date, doc.submission_date),
        firstSummaryValue(doc.application_number, doc.application_no),
        highPriority ? "high" : "medium",
      );
    });

  const fdaGuid = (data.fda_guid || {}) as Record<string, unknown>;
  recordArray(fdaGuid.guidance_documents)
    .slice(0, 4)
    .forEach((doc) => {
      add(
        "FDA PSG",
        "BE 指导原则",
        firstSummaryValue(doc.title, doc.name, doc.document_name),
        firstSummaryValue(doc.pdf_url, doc.url, doc.href),
        firstSummaryValue(doc.revision_date, doc.date),
        firstSummaryValue(doc.dosage_form, doc.route),
        "high",
      );
    });

  const healthCanada = (data.health_canada || {}) as Record<string, unknown>;
  recordArray(healthCanada.products)
    .slice(0, 6)
    .forEach((product) => {
      const documentLinks = recordArray(product.document_links);
      const monographLink = documentLinks.find((link) =>
        String(link.kind || "") === "product_monograph"
      );
      const dpdLink = documentLinks.find((link) =>
        String(link.kind || "") === "dpd_product" ||
        String(link.url || "").includes("dpd-bdpp")
      );
      const monographUrl = firstSummaryValue(
        product.product_monograph_url,
        monographLink?.url,
      );
      const dpdUrl = firstSummaryValue(product.source_url, dpdLink?.url);

      if (monographUrl) {
        add(
          "Health Canada",
          "Product Monograph",
          `${firstSummaryValue(product.brand_name, product.descriptor, "Health Canada product")} Product Monograph`,
          monographUrl,
          firstSummaryValue(product.product_monograph_date, product.last_update_date),
          firstSummaryValue(product.din),
          "high",
        );
      } else if (dpdUrl) {
        add(
          "Health Canada",
          "DPD 药品记录",
          firstSummaryValue(product.brand_name, product.descriptor, "Health Canada product"),
          dpdUrl,
          firstSummaryValue(product.last_update_date, product.original_market_date),
          firstSummaryValue(product.din),
          "medium",
        );
      }
    });
  add("FDA PSG", "BE 指导原则", fdaGuid.guidance_title || fdaGuid.title, fdaGuid.pdf_url || fdaGuid.psg_url, fdaGuid.date, undefined, "high");

  const emaProduct = firstSummaryRecord((data.ema as Record<string, unknown> | undefined)?.products);
  recordArray(emaProduct.documents)
    .slice(0, 4)
    .forEach((doc) => {
      add(
        "EMA",
        firstSummaryValue(doc.type, doc.document_type, "EPAR"),
        firstSummaryValue(doc.title, doc.name, doc.label),
        firstSummaryValue(doc.url, doc.href, doc.pdf_url),
        doc.date,
        undefined,
        /assessment/i.test(firstSummaryValue(doc.title, doc.type)) ? "high" : "medium",
      );
    });
  add("EMA", "Assessment Report", "Assessment Report", emaProduct.assessment_report_url, emaProduct.authorization_date, undefined, "high");
  add("EMA", "Product Information", "Product Information (SmPC/PL)", emaProduct.product_information_url || emaProduct.smpc_url, undefined, undefined, "high");

  recordArray((data.pmda as Record<string, unknown> | undefined)?.products)
    .slice(0, 2)
    .forEach((product) => {
      const packageInsert = pickPmdaPackageInsert(product);
      if (packageInsert) {
        add("PMDA", "说明书", packageInsert.label, packageInsert.url, undefined, firstSummaryValue(product.product_name, product.title), "high");
      }
      pickPmdaReviewDocuments(product)
        .slice(0, 4)
        .forEach((doc) => {
          add("PMDA", "审评文件", doc.label, doc.url, undefined, firstSummaryValue(product.product_name, product.title), "medium");
        });
    });

  recordArray((data.pubmed as Record<string, unknown> | undefined)?.articles)
    .slice(0, 3)
    .forEach((article) => {
      add(
        "PubMed",
        "文献",
        firstSummaryValue(article.title, article.article_title),
        firstSummaryValue(article.url, article.pubmed_url, article.link),
        firstSummaryValue(article.publication_date, article.year),
        firstSummaryValue(article.journal),
        "medium",
      );
    });

  const dailymed = (data.dailymed || {}) as Record<string, unknown>;
  add("DailyMed", "说明书", firstSummaryValue(dailymed.title, dailymed.drug_name, "DailyMed Label"), dailymed.label_url || dailymed.detail_url || dailymed.source_url || dailymed.url, undefined, undefined, "medium");

  const pubchem = (data.pubchem || {}) as Record<string, unknown>;
  recordArray(pubchem.compounds)
    .slice(0, 3)
    .forEach((compound) => {
      add(
        "PubChem",
        "分子结构",
        firstSummaryValue(compound.title, compound.name, compound.iupac_name),
        firstSummaryValue(compound.pubchem_url, compound.url, compound.structure_url),
        undefined,
        firstSummaryValue(compound.molecular_formula, compound.formula),
        "standard",
      );
    });

  return links.sort((a, b) => {
    const rank = { high: 0, medium: 1, standard: 2 };
    return rank[a.priority || "standard"] - rank[b.priority || "standard"];
  });
}

function PkMetricsSummary({ data }: { data: Record<string, unknown> }) {
  const metrics = Array.isArray(data.pk_metrics)
    ? (data.pk_metrics as PkMetric[]).filter(
        (metric) => metric && metric.parameter && metric.value,
      )
    : [];

  if (metrics.length === 0) {
    const compactPk = getCompactPk(data);
    return compactPk
      ? <DataRow label="关键 PK 指标" value={compactPk} />
      : null;
  }

  const grouped = metrics.reduce<Map<string, PkMetric[]>>((result, metric) => {
    const current = result.get(metric.parameter) || [];
    current.push(metric);
    result.set(metric.parameter, current);
    return result;
  }, new Map());

  return (
    <div className="data-row group">
      <span className="data-label">关键 PK 指标</span>
      <div className="min-w-0 flex-1 overflow-hidden" data-testid="pk-metrics">
        <div className="overflow-x-auto rounded border border-border">
          <div className="min-w-[34rem] divide-y divide-border">
            {Array.from(grouped.entries()).map(([parameter, parameterMetrics]) => (
              <div
                key={parameter}
                data-testid={`pk-metric-${parameter}`}
                className="grid grid-cols-[minmax(10rem,0.9fr)_minmax(18rem,2fr)] gap-4 px-3 py-2.5"
              >
                <div className="flex items-baseline gap-2">
                  <span className="text-base font-semibold text-foreground">
                    <PkNotation metric={parameterMetrics[0]} />
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {PK_PARAMETER_LABELS[parameter] || parameter}
                  </span>
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1.5">
                  {parameterMetrics.map((metric, index) => (
                    <span
                      key={`${metric.context || "general"}-${metric.value}-${index}`}
                      className="text-sm text-foreground"
                      title={metric.source_text || undefined}
                    >
                      {metric.context && (
                        <span className="text-muted-foreground">
                          {metric.context}:{" "}
                        </span>
                      )}
                      <span className="font-medium tabular-nums">
                        {metric.qualifier || ""}
                        {metric.value}
                      </span>
                      {metric.unit && <>{" "}{metric.unit}</>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          仅展示 FDA 标签中明确报告的数值和单位；标签未提供绝对{" "}
          <i>C</i><sub>max</sub> 或 <i>AUC</i> 时不作推算。
        </p>
      </div>
    </div>
  );
}

function FdaSummary({
  data,
  showSourceLink = true,
}: {
  data: Record<string, unknown>;
  showSourceLink?: boolean;
}) {
  const strengths = getFdaStrengths(data);
  const applicationNumbers = getFdaApplicationNumbers(data);
  const referenceProducts = recordArray(data.reference_product_applications).slice(0, 8);
  const labelUrl = String(data.dailymed_label_url || data.label_url || data.source_url || "").trim();
  const drugsFdaUrl = String(data.drugsfda_url || "").trim();
  const openFdaUrl = String(data.openfda_url || "").trim();

  return (
    <div className="mt-3 space-y-4">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Regulatory identity
        </p>
        <DataRow label="商品名" value={String(data.brand_name || "—")} highlight />
        <DataRow label="通用名 / 活性成分" value={String(data.generic_name || "—")} />
        <DataRow label="持证商 / 申请人" value={String(data.manufacturer || "—")} />
        <DataRow label="原研 FDA 申请号" value={applicationNumbers || String(data.application_number || "—")} />
      </div>

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Dosage and strength
        </p>
        <DataRow label="剂型 / 给药途径" value={uniqueJoined([data.dosage_form, data.route]) || "—"} />
        <DataRow label="本次匹配规格" value={String(data.approval_strength || "—")} highlight />
        <DataRow label="同剂型官方规格" value={strengths || "—"} />
      </div>

      <PkMetricsSummary data={data} />

      {referenceProducts.length > 0 && (
        <div className="data-row group">
          <span className="data-label">参照产品</span>
          <div className="min-w-0 flex-1 overflow-x-auto rounded border border-border">
            <table className="w-full min-w-[44rem] text-left text-xs">
              <thead className="bg-muted/40 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 font-medium">申请号</th>
                  <th className="px-3 py-2 font-medium">商品名</th>
                  <th className="px-3 py-2 font-medium">申请人</th>
                  <th className="px-3 py-2 font-medium">规格</th>
                  <th className="px-3 py-2 font-medium">状态</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {referenceProducts.map((product, index) => (
                  <tr key={`${product.application_number || index}-${index}`}>
                    <td className="px-3 py-2 font-medium text-foreground">
                      {String(product.application_number || "—")}
                    </td>
                    <td className="px-3 py-2 text-foreground">
                      {String(product.brand_name || "—")}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {String(product.sponsor_name || "—")}
                    </td>
                    <td className="px-3 py-2 text-foreground">
                      {String(product.strength || "—")}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground">
                      {String(product.marketing_status || "—")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {labelUrl && <OfficialDocumentLink href={labelUrl} label="DailyMed FDA Label" />}
        {drugsFdaUrl && <OfficialDocumentLink href={drugsFdaUrl} label="Drugs@FDA 申请页" />}
        {showSourceLink && openFdaUrl && (
          <OfficialDocumentLink href={openFdaUrl} label="openFDA Label JSON" />
        )}
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        本模块只保留临床试验设计常用的监管身份、剂型规格、原研申请号和标签中明确报告的 PK 信息；完整适应症、安全性和药理内容以官方 Label 为准。
      </p>
    </div>
  );
}

function DailyMedSummary({ data }: { data: Record<string, unknown> }) {
  const labelUrl = String(data.label_url || data.detail_url || data.source_url || data.url || "").trim();
  const pkText = truncateClinicalText(data.pk_parameters, 360);
  const mechanism = truncateClinicalText(data.mechanism || data.mechanism_of_action, 220);
  const specialPopulations = truncateClinicalText(data.special_populations, 220);

  return (
    <div className="mt-3 space-y-4">
      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          FDA SPL label evidence
        </p>
        <DataRow label="说明书标题" value={String(data.title || data.drug_name || "—")} highlight />
        <DataRow label="SPL Set ID" value={String(data.spl_set_id || data.setid || data.set_id || "—")} />
        <DataRow label="剂型 / 给药途径" value={uniqueJoined([data.dosage_form, data.route]) || ""} />
      </div>

      <PkMetricsSummary data={data} />

      <div>
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Clinical pharmacology notes
        </p>
        <DataRow label="PK 原文摘要" value={pkText} />
        <DataRow label="作用机制" value={mechanism} />
        <DataRow label="特殊人群" value={specialPopulations} />
      </div>

      <div className="flex flex-wrap gap-2">
        {labelUrl && <OfficialDocumentLink href={labelUrl} label="DailyMed 官方说明书" />}
      </div>

      <p className="text-xs leading-relaxed text-muted-foreground">
        DailyMed 为 FDA SPL 标签来源。本页面只展示临床方案设计常用摘要；给药、相互作用、安全性和完整 PK 描述请打开官方说明书核对。
      </p>
    </div>
  );
}

function SummaryField({
  label,
  value,
  emphasize = false,
}: {
  label: string;
  value: unknown;
  emphasize?: boolean;
}) {
  const text = String(value || "").trim();
  if (!text) return null;
  return (
    <div className="min-w-0 py-1">
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd
        className={`mt-1 break-words text-sm leading-relaxed ${
          emphasize ? "font-medium text-primary" : "text-foreground"
        }`}
      >
        {text}
      </dd>
    </div>
  );
}

function resolveDocumentHref(href: string): string {
  const value = String(href || "").trim();
  if (!value) return "";
  if (/^(?:https?:|mailto:|blob:|data:)/i.test(value)) return value;
  if (value.startsWith("/")) {
    return `${API_BASE.replace(/\/$/, "")}${value}`;
  }
  return value;
}

function OfficialDocumentLink({
  href,
  label,
}: {
  href: string;
  label: string;
}) {
  const resolvedHref = resolveDocumentHref(href);
  if (!resolvedHref) return null;
  const isCachedPdf =
    href.trim().startsWith("/api/local-file/") ||
    resolvedHref.includes("/api/local-file/");
  return (
    <a
      href={resolvedHref}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex min-h-9 items-center gap-2 rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
    >
      <FileText className="h-4 w-4 flex-none" aria-hidden="true" />
      <span className="break-words">{label}</span>
      <span
        className={`rounded-sm px-1.5 py-0.5 text-[11px] font-semibold ${
          isCachedPdf
            ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
            : "bg-muted text-muted-foreground"
        }`}
      >
        {isCachedPdf ? "本地 PDF" : "官方链接"}
      </span>
      <ExternalLink className="h-3.5 w-3.5 flex-none" aria-hidden="true" />
    </a>
  );
}

function recordArray(value: unknown): Record<string, unknown>[] {
  return Array.isArray(value)
    ? value.filter(
        (item): item is Record<string, unknown> =>
          Boolean(item) && typeof item === "object" && !Array.isArray(item),
      )
    : [];
}

function textList(value: unknown): string {
  return Array.isArray(value)
    ? uniqueJoined(value)
    : String(value || "").trim();
}

function markdownCell(value: unknown): string {
  const text = String(value ?? "").trim().replace(/\|/g, " / ");
  return text || "-";
}

function firstText(record: Record<string, unknown>, keys: string[]): string {
  for (const key of keys) {
    const value = String(record[key] ?? "").trim();
    if (value) return value;
  }
  return "";
}

function buildCdeTrialsMarkdownTable(data: Record<string, unknown>): string {
  const registrations = recordArray(data.registrations);
  const trials = registrations.length > 0 ? registrations : recordArray(data.trials);
  if (trials.length === 0) return "";

  const header = [
    "序号",
    "登记号",
    "药物名称",
    "申请人",
    "试验状态",
    "公示日期",
    "试验类别",
    "周期",
    "临床研究机构",
    "入组人数",
  ];
  const lines = [
    `| ${header.join(" | ")} |`,
    `| ${header.map(() => "---").join(" | ")} |`,
  ];

  trials.slice(0, 20).forEach((trial, index) => {
    const cells = [
      String(index + 1),
      firstText(trial, ["reg_no", "registration_no", "trial_id"]),
      firstText(trial, ["drug_name", "drug"]),
      firstText(trial, ["applicant_name", "sponsor", "applicant"]),
      firstText(trial, ["trial_status", "status"]),
      firstText(trial, ["first_public_date", "registration_date", "public_date"]),
      firstText(trial, ["trial_category", "category"]),
      firstText(trial, ["period", "cycle"]),
      firstText(trial, [
        "clinical_institution",
        "clinical_institutions",
        "institution",
      ]),
      firstText(trial, ["planned_enrollment", "enrollment", "sample_size"]),
    ];
    lines.push(`| ${cells.map(markdownCell).join(" | ")} |`);
  });

  if (trials.length > 20) {
    lines.push("");
    lines.push(`*共 ${trials.length} 条记录，显示前 20 条*`);
  }

  return lines.join("\n");
}

const PMDA_DOSAGE_FORMS = [
  { marker: "口腔内崩壊錠", label: "口腔崩解片" },
  { marker: "OD錠", label: "口腔崩解片" },
  { marker: "フィルムコーティング錠", label: "薄膜衣片" },
  { marker: "徐放錠", label: "缓释片" },
  { marker: "錠", label: "片剂" },
  { marker: "カプセル", label: "胶囊" },
  { marker: "顆粒", label: "颗粒剂" },
  { marker: "細粒", label: "细粒剂" },
  { marker: "散", label: "散剂" },
  { marker: "ドライシロップ", label: "干糖浆剂" },
  { marker: "シロップ", label: "糖浆剂" },
  { marker: "液", label: "液体制剂" },
  { marker: "注", label: "注射剂" },
  { marker: "点滴", label: "注射剂" },
  { marker: "貼付", label: "贴剂" },
  { marker: "軟膏", label: "软膏剂" },
  { marker: "クリーム", label: "乳膏剂" },
  { marker: "点眼", label: "滴眼剂" },
  { marker: "点鼻", label: "鼻用制剂" },
  { marker: "吸入", label: "吸入制剂" },
];

const PMDA_DOCUMENT_KIND_LABELS: Record<string, string> = {
  package_insert: "说明书",
  interview_form: "IF",
  review_report: "审评报告",
  application_summary: "申请资料概要",
  detail_page: "详情页",
  other_pdf: "其他PDF",
};

const PMDA_DOCUMENT_KIND_ORDER: Record<string, number> = {
  package_insert: 0,
  interview_form: 1,
  review_report: 2,
  application_summary: 3,
  detail_page: 4,
  other_pdf: 5,
};

function pickPmdaPackageInsert(product: Record<string, unknown>) {
  const links = recordArray(product.document_links);
  const direct = String(product.package_insert_url || "");
  const fromLinks = links.find((link) => {
    const label = String(link.label || "");
    const url = String(link.url || "");
    return (
      url.includes("ResultDataSetPDF") ||
      label.startsWith("PDF(") ||
      label.includes("添付文書")
    );
  });
  return {
    label: String(fromLinks?.label || "说明书 PDF"),
    url: direct || String(fromLinks?.url || ""),
  };
}

function pickPmdaReviewDocuments(product: Record<string, unknown>) {
  const links = recordArray(product.document_links);
  const direct = String(product.review_report_url || "");
  const docs = links
    .filter((link) => {
      const label = String(link.label || "");
      const url = String(link.url || "");
      return (
        url.toLowerCase().includes(".pdf") &&
        (label.includes("審査報告書") ||
          label.includes("审查报告") ||
          label.includes("審査") ||
          url.includes("/drugs/"))
      );
    })
    .map((link) => {
      const label = String(link.label || "").trim();
      return {
        label: label && /^\d+$/.test(label) ? `审评文件 ${label}` : label || "审评文件 PDF",
        url: String(link.url || ""),
      };
    });

  if (direct && !docs.some((doc) => doc.url === direct)) {
    docs.unshift({ label: "审评文件 PDF", url: direct });
  }

  return docs.filter((doc) => doc.url).slice(0, 4);
}

function normalizePmdaDocumentLinks(value: unknown) {
  const docs = recordArray(value)
    .map((doc, index) => {
      const kind = String(doc.kind || "").trim() || "other_pdf";
      const url = String(doc.url || "").trim();
      const label =
        String(doc.label || "").trim() ||
        PMDA_DOCUMENT_KIND_LABELS[kind] ||
        `PMDA文档 ${index + 1}`;
      return { kind, label, url };
    })
    .filter((doc) => doc.url);

  const seen = new Set<string>();
  return docs
    .filter((doc) => {
      const key = doc.url.toLowerCase();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .sort(
      (a, b) =>
        (PMDA_DOCUMENT_KIND_ORDER[a.kind] ?? 99) -
        (PMDA_DOCUMENT_KIND_ORDER[b.kind] ?? 99),
    );
}

function getPmdaProductSegment(product: Record<string, unknown>): string {
  const description = String(product.description || "");
  const title = String(product.product_name || product.title || "").trim();
  if (!description) return "";

  let segment = description;
  if (title && segment.startsWith(title)) {
    segment = segment.slice(title.length).trim();
  }
  segment = segment
    .split(/製造販売元|販売元|PDF\(|HTML|XML|審査報告書|申請資料概要/)[0]
    .replace(/\s+/g, " ")
    .trim();

  return segment;
}

function parsePmdaVariantName(value: string) {
  const text = String(value || "").replace(/\s+/g, " ").trim();
  const form = PMDA_DOSAGE_FORMS.find((item) => text.includes(item.marker));
  const specMatch = text.match(
    /(\d+(?:\.\d+)?\s*(?:mg|g|μg|µg|mcg|mL|ml|％|%)(?:\s*\/\s*\d+(?:\.\d+)?\s*(?:mg|g|μg|µg|mcg|mL|ml|％|%))?(?:分包|包|錠|カプセル|粒|瓶|袋)?)/i,
  );
  const specification = specMatch ? specMatch[1].replace(/\s+/g, "") : "";
  const productName = specification
    ? text.replace(specMatch?.[1] || "", "").trim()
    : text;

  return {
    productName: productName || text || "PMDA 药品",
    specification: specification || "请查看说明书",
    dosageFormCode: form?.marker || "",
    dosageFormZh: form?.label || "",
    dosageForm: form ? `${form.marker}（${form.label}）` : "请查看说明书",
  };
}

function buildPmdaRows(product: Record<string, unknown>) {
  const productSegment = getPmdaProductSegment(product);
  const variants = productSegment
    ? productSegment
        .split(/[／/、,，;；]/)
        .map((item) => item.trim())
        .filter(Boolean)
    : [];
  const fallbackName = String(product.product_name || product.title || product.name || "PMDA 药品");
  const packageInsert = pickPmdaPackageInsert(product);
  const reviewDocuments = pickPmdaReviewDocuments(product);
  const sourceItems = variants.length > 0 ? variants : [fallbackName];

  return sourceItems.map((variant, index) => ({
    key: `${fallbackName}-${variant}-${index}`,
    ...parsePmdaVariantName(variant),
    genericName: fallbackName,
    manufacturer: String(product.marketing_authorization_holder || ""),
    sourceTitle: fallbackName,
    sourceUrl: String(product.url || ""),
    packageInsert,
    documentLinks: [
      ...(packageInsert.url ? [{ kind: "package_insert", label: "说明书", url: packageInsert.url }] : []),
      ...reviewDocuments.map((doc) => ({ kind: "review_report", ...doc })),
    ],
    reviewDocuments,
  }));
}

function normalizePmdaRows(data: Record<string, unknown>) {
  const backendRows = recordArray(data.product_rows);
  if (backendRows.length > 0) {
    return backendRows.map((row, index) => {
      const displayName =
        firstText(row, ["display_name", "product_name", "brand_name", "name"]) ||
        "PMDA 药品";
      const genericName = firstText(row, ["generic_name", "source_title"]);
      const documentLinks = normalizePmdaDocumentLinks(row.document_links);
      const reviewDocuments = documentLinks.filter((doc) =>
        ["review_report", "application_summary"].includes(doc.kind),
      );

      return {
        key: `${displayName}-${row.specification || ""}-${index}`,
        productName: displayName,
        genericName,
        specification: firstText(row, ["specification"]) || "请查看说明书",
        dosageForm:
          firstText(row, ["dosage_form_display"]) ||
          firstText(row, ["dosage_form_zh"]) ||
          "请查看说明书",
        manufacturer: firstText(row, ["marketing_authorization_holder", "holder"]),
        sourceTitle: firstText(row, ["source_title"]),
        sourceUrl: firstText(row, ["source_url"]),
        packageInsert: {
          label: "说明书",
          url:
            firstText(row, ["package_insert_url"]) ||
            documentLinks.find((doc) => doc.kind === "package_insert")?.url ||
            "",
        },
        documentLinks,
        reviewDocuments,
      };
    });
  }

  return recordArray(data.products).flatMap(buildPmdaRows);
}

function PmdaProductsSummary({ data }: { data: Record<string, unknown> }) {
  const products = recordArray(data.products);
  const rows = normalizePmdaRows(data);

  if (products.length === 0 && rows.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        PMDA 暂无可展示的药品记录。
      </p>
    );
  }

  const summary = data.document_summary as Record<string, unknown> | undefined;
  const packageCount = Number(summary?.package_insert || 0);
  const ifCount = Number(summary?.interview_form || 0);
  const reviewCount =
    Number(summary?.review_report || 0) + Number(summary?.application_summary || 0);

  return (
    <div className="mt-3">
      <div className="border-b border-border pb-3 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span>
            PMDA 记录{" "}
            <strong className="text-foreground">
              {String(data.total_count ?? products.length)}
            </strong>{" "}
            条
          </span>
          <span>
            规格/剂型{" "}
            <strong className="text-foreground">
              {String(data.product_variant_count ?? rows.length)}
            </strong>{" "}
            项
          </span>
          {(packageCount > 0 || ifCount > 0 || reviewCount > 0) && (
            <span>
              关键文档：说明书 {packageCount}、IF {ifCount}、审评/申请资料 {reviewCount}
            </span>
          )}
          {data.search_term && (
            <span>
              检索词：<strong className="text-foreground">{String(data.search_term)}</strong>
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded-md border border-border">
        <table className="min-w-[980px] w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="w-12 px-3 py-2.5 font-medium">序号</th>
              <th className="px-3 py-2.5 font-medium">药品名称（商品名）</th>
              <th className="px-3 py-2.5 font-medium">规格</th>
              <th className="px-3 py-2.5 font-medium">剂型</th>
              <th className="px-3 py-2.5 font-medium">持证/制造销售方</th>
              <th className="px-3 py-2.5 font-medium">关键官方文档</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {rows.map((row, index) => (
              <tr key={row.key} className="hover:bg-muted/20">
                <td className="px-3 py-3 text-muted-foreground">{index + 1}</td>
                <td className="max-w-[260px] px-3 py-3 font-medium text-foreground">
                  <span className="break-words">{row.productName}</span>
                  {row.genericName && row.genericName !== row.productName && (
                    <span className="mt-1 block text-xs font-normal text-muted-foreground">
                      {row.genericName}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 font-mono text-xs text-foreground">
                  {row.specification}
                </td>
                <td className="px-3 py-3 text-muted-foreground">
                  {row.dosageForm}
                </td>
                <td className="max-w-[220px] px-3 py-3 text-muted-foreground">
                  {row.manufacturer || "-"}
                </td>
                <td className="px-3 py-3">
                  {row.documentLinks.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {row.documentLinks.slice(0, 6).map((doc, docIndex) => (
                        <a
                          key={`${doc.url}-${docIndex}`}
                          href={doc.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2 py-1 text-xs text-foreground hover:border-primary/50 hover:text-primary"
                        >
                          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                          {doc.label || PMDA_DOCUMENT_KIND_LABELS[doc.kind] || `文档${docIndex + 1}`}
                        </a>
                      ))}
                    </div>
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        说明书用于适应症、用法用量、安全性和包装规格核对；IF、审评报告和申请资料概要可用于制剂特征、
        药代动力学、BE/桥接设计依据追溯。若 PMDA 页面未提供直接 PDF，请以详情页中的最新官方文件为准。
      </p>
    </div>
  );
}

export function HealthCanadaProductsSummary({ data }: { data: Record<string, unknown> }) {
  const products = recordArray(data.products);
  const marketSummary =
    data.market_summary &&
    typeof data.market_summary === "object" &&
    !Array.isArray(data.market_summary)
      ? (data.market_summary as Record<string, unknown>)
      : {};
  const monographSummary =
    data.monograph_summary &&
    typeof data.monograph_summary === "object" &&
    !Array.isArray(data.monograph_summary)
      ? (data.monograph_summary as Record<string, unknown>)
      : {};

  if (products.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        暂未检索到可展示的 Health Canada DPD 药品记录。
      </p>
    );
  }

  const productTypeLabels: Record<string, string> = {
    originator_candidate: "原研/参照候选",
    generic_candidate: "仿制药候选",
    follow_on_candidate: "仿制/后续上市候选",
    earliest_market_candidate: "最早上市候选",
    undetermined: "性质待核实",
  };
  const activeProductCount = Number(marketSummary.active_product_count || 0);
  const activeBrandCount = Number(marketSummary.active_brand_count || 0);
  const genericBrandCount = Number(marketSummary.generic_brand_count || 0);
  const monographAvailableCount = Number(monographSummary.available_count || 0);
  const matchedCount = Number(data.matched_count ?? products.length);
  const returnedCount = Number(data.returned_count ?? products.length);
  const nocSearchUrl = String(
    data.noc_search_url || marketSummary.noc_search_url || ""
  ).trim();

  return (
    <div className="mt-3">
      <div className="border-y border-border py-3">
        <div className="grid grid-cols-2 gap-x-5 gap-y-3 md:grid-cols-4">
          {[
            ["在售/已批准产品", activeProductCount],
            ["有效品牌", activeBrandCount],
            ["仿制/后续候选品牌", genericBrandCount],
            ["Product Monograph", `${monographAvailableCount} 份`],
          ].map(([label, value]) => (
            <div key={String(label)} className="min-w-0 md:border-r md:border-border last:border-r-0">
              <div className="text-xs text-muted-foreground">{label}</div>
              <div className="mt-1 text-lg font-semibold text-foreground">{String(value)}</div>
            </div>
          ))}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <span>
            匹配 <strong className="text-foreground">{matchedCount}</strong> 条，展示{" "}
            <strong className="text-foreground">{returnedCount}</strong> 条
          </span>
          {marketSummary.generic_availability && (
            <span className="font-medium text-foreground">
              {String(marketSummary.generic_availability)}
            </span>
          )}
          {data.result_truncated === true && <span>其余低相关记录已折叠。</span>}
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded-md border border-border">
        <table className="min-w-[1260px] w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="w-12 px-3 py-2.5 font-medium">序号</th>
              <th className="px-3 py-2.5 font-medium">DIN</th>
              <th className="px-3 py-2.5 font-medium">属性推定</th>
              <th className="px-3 py-2.5 font-medium">品牌名</th>
              <th className="px-3 py-2.5 font-medium">活性成分 / 规格</th>
              <th className="px-3 py-2.5 font-medium">剂型 / 给药途径</th>
              <th className="px-3 py-2.5 font-medium">公司</th>
              <th className="px-3 py-2.5 font-medium">上市状态</th>
              <th className="px-3 py-2.5 font-medium">官方文件</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {products.map((product, index) => {
              const links = recordArray(product.document_links);
              const ingredientText =
                textList(product.active_ingredient_names) ||
                textList(product.active_ingredients);
              const specification = String(product.specification || "").trim();
              const typeCode = String(product.product_type_code || "undetermined");
              const productType =
                productTypeLabels[typeCode] || String(product.product_type || "性质待核实");
              const confidence = String(product.classification_confidence || "").trim();
              const classificationBasis = String(product.classification_basis || "").trim();
              const statusCategory = String(product.status_category || "other");
              const monographUrl = String(product.product_monograph_url || "").trim();
              const dpdLink = links.find((link) => String(link.kind || "") === "dpd_product") ||
                links.find((link) => String(link.url || "").includes("dpd-bdpp"));
              const monographStatus = String(product.monograph_lookup_status || "");

              return (
                <tr
                  key={String(product.drug_code ?? product.din ?? index)}
                  className="align-top hover:bg-muted/20"
                >
                  <td className="px-3 py-3 text-muted-foreground">{index + 1}</td>
                  <td className="px-3 py-3 font-mono text-xs text-foreground">
                    {String(product.din || "-")}
                  </td>
                  <td className="max-w-[210px] px-3 py-3">
                    <span className={`inline-flex rounded px-2 py-1 text-xs font-medium ${
                      typeCode === "originator_candidate"
                        ? "bg-primary/10 text-primary"
                        : typeCode === "generic_candidate" || typeCode === "follow_on_candidate"
                          ? "bg-success/10 text-success"
                          : "bg-muted text-muted-foreground"
                    }`}>
                      {productType}
                    </span>
                    {(confidence || classificationBasis) && (
                      <div className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                        {confidence && <span>置信度：{confidence}</span>}
                        {classificationBasis && <div>{classificationBasis}</div>}
                      </div>
                    )}
                  </td>
                  <td className="max-w-[210px] px-3 py-3 font-medium text-foreground">
                    <span className="break-words">
                      {String(product.brand_name || product.descriptor || "Health Canada product")}
                    </span>
                    {product.descriptor && (
                      <div className="mt-1 text-xs font-normal text-muted-foreground">
                        {String(product.descriptor)}
                      </div>
                    )}
                  </td>
                  <td className="max-w-[300px] px-3 py-3 text-foreground">
                    <span className="break-words">{ingredientText || "-"}</span>
                    {specification && (
                      <div className="mt-1 text-xs text-muted-foreground">规格：{specification}</div>
                    )}
                  </td>
                  <td className="max-w-[190px] px-3 py-3 text-muted-foreground">
                    <div>{String(product.dosage_form || "-")}</div>
                    {product.route && <div className="mt-1 text-xs">{String(product.route)}</div>}
                  </td>
                  <td className="max-w-[220px] px-3 py-3 text-muted-foreground">
                    <span className="break-words">{String(product.company || "-")}</span>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`inline-flex rounded px-2 py-1 text-xs font-medium ${
                      statusCategory === "marketed" || statusCategory === "approved"
                        ? "bg-success/10 text-success"
                        : statusCategory === "cancelled"
                          ? "bg-destructive/10 text-destructive"
                          : "bg-muted text-muted-foreground"
                    }`}>
                      {String(product.status || "-")}
                    </span>
                    {product.original_market_date && (
                      <div className="mt-1.5 text-xs text-muted-foreground">
                        首次上市：{String(product.original_market_date)}
                      </div>
                    )}
                  </td>
                  <td className="min-w-[190px] px-3 py-3">
                    <div className="flex flex-col items-start gap-2">
                      {monographUrl ? (
                        <a
                          href={resolveDocumentHref(monographUrl)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                        >
                          <FileText className="h-3.5 w-3.5" aria-hidden="true" />
                          Product Monograph PDF
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {monographStatus.startsWith("lookup_error:")
                            ? "PDF 链接核验失败"
                            : "官方未提供电子版"}
                        </span>
                      )}
                      {dpdLink && (
                        <a
                          href={resolveDocumentHref(String(dpdLink.url || ""))}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-primary hover:underline"
                        >
                          DPD 产品页
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-3 border-l-2 border-warning/60 pl-3 text-xs leading-relaxed text-muted-foreground">
        <p>
          {String(
            marketSummary.method_note ||
              "原研/仿制属性为辅助推定，不是 Health Canada 的正式监管认定。"
          )}
        </p>
        {nocSearchUrl && (
          <a
            href={resolveDocumentHref(nocSearchUrl)}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 inline-flex items-center gap-1.5 font-medium text-primary hover:underline"
          >
            打开 Health Canada NOC 数据库核对 NDS/ANDS 与 Canadian Reference Product
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        )}
      </div>
    </div>
  );
}

function EmaClinicalPkEvidence({ evidence }: { evidence: Record<string, unknown> }) {
  const status = String(evidence.status || "");
  const studies = recordArray(evidence.studies);
  const parameters = recordArray(evidence.parameter_summary);
  const documents = recordArray(evidence.documents_analyzed);
  const processing = fdaReviewRecord(evidence.processing);
  const topics = fdaReviewRecord(evidence.topic_summaries);
  const gaps = Array.isArray(evidence.evidence_gaps)
    ? evidence.evidence_gaps.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const managerNotes = Array.isArray(evidence.manager_notes)
    ? evidence.manager_notes.map((item) => String(item || "").trim()).filter(Boolean)
    : [];
  const topicDefinitions = [
    ["food_effect", "食物影响"],
    ["ddi", "药物相互作用（DDI）"],
    ["special_populations", "特殊人群"],
    ["dose_proportionality", "剂量比例关系"],
    ["accumulation", "蓄积与稳态"],
    ["bioavailability_be", "生物利用度 / 生物等效性"],
  ] as const;
  const topicRows: Array<Record<string, unknown> & { label: string }> = topicDefinitions.flatMap(([key, label]) =>
    recordArray(topics[key]).map((item) => ({ ...item, label })),
  );
  const unavailable = ["disabled", "not_available", "error"].includes(status);
  const statusLabel = status === "success"
    ? "已提取"
    : status === "partial"
      ? "部分证据"
      : "需人工复核";
  const statusClass = status === "success"
    ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
    : "bg-amber-500/10 text-amber-700 dark:text-amber-300";

  return (
    <section aria-labelledby="ema-clinical-pk-heading" className="border-t border-border pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 id="ema-clinical-pk-heading" className="text-sm font-semibold text-foreground">
            EMA 临床药代动力学证据摘要
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Assessment Report 与 Product Information 自动摘取，供方案可行性、终点选择和证据缺口初筛
          </p>
        </div>
        <span className={`rounded-sm px-2 py-1 text-xs font-semibold ${statusClass}`}>
          {statusLabel}
        </span>
      </div>

      <p className="mt-3 text-xs leading-relaxed text-foreground">
        {fdaReviewText(evidence.executive_summary, "暂未形成可审计的 EMA 临床 PK 摘要。")}
      </p>

      {!unavailable && (
        <dl className="mt-3 grid grid-cols-2 border-y border-border bg-muted/10 text-xs sm:grid-cols-4">
          <div className="px-3 py-2.5">
            <dt className="text-muted-foreground">分析文件</dt>
            <dd className="mt-1 font-semibold text-foreground">{documents.length}</dd>
          </div>
          <div className="border-l border-border px-3 py-2.5">
            <dt className="text-muted-foreground">研究线索</dt>
            <dd className="mt-1 font-semibold text-foreground">{studies.length}</dd>
          </div>
          <div className="border-l border-border px-3 py-2.5">
            <dt className="text-muted-foreground">参数证据</dt>
            <dd className="mt-1 font-semibold text-foreground">{parameters.length}</dd>
          </div>
          <div className="border-l border-border px-3 py-2.5">
            <dt className="text-muted-foreground">相关页数</dt>
            <dd className="mt-1 font-semibold text-foreground">
              {String(processing.pages_with_evidence ?? processing.pages_relevant ?? 0)}
            </dd>
          </div>
        </dl>
      )}

      {studies.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-xs font-semibold text-foreground">临床 PK / 生物药剂学研究矩阵</h4>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-[1040px] w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">研究 / 类型</th>
                  <th className="px-3 py-2 text-left font-medium">设计与人群</th>
                  <th className="px-3 py-2 text-left font-medium">给药</th>
                  <th className="px-3 py-2 text-left font-medium">关键参数 / 结论</th>
                  <th className="px-3 py-2 text-right font-medium">证据出处</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {studies.map((study, index) => {
                  const source = fdaReviewRecord(study.source);
                  const pages = Array.isArray(source.pages) ? source.pages : [];
                  const firstPage = pages[0];
                  const lastPage = pages[pages.length - 1];
                  const pageLabel = pages.length > 1
                    ? `PDF 第 ${String(firstPage)}–${String(lastPage)} 页`
                    : `PDF 第 ${String(firstPage || "-")} 页`;
                  const findings = Array.isArray(study.key_findings)
                    ? study.key_findings.map((item) => String(item || "")).filter(Boolean)
                    : [];
                  const pkParameters = Array.isArray(study.pk_parameters)
                    ? study.pk_parameters.map((item) => String(item || "")).filter(Boolean)
                    : [];
                  return (
                    <tr key={`${String(study.study_id || "study")}-${index}`} className="align-top hover:bg-muted/20">
                      <td className="px-3 py-2.5">
                        <div className="font-semibold text-foreground">{fdaReviewText(study.study_id, `研究 ${index + 1}`)}</div>
                        {study.study_title && <div className="mt-1 max-w-[260px] leading-relaxed">{fdaReviewText(study.study_title)}</div>}
                        <div className="mt-1 text-muted-foreground">{fdaReviewText(study.study_type)}</div>
                      </td>
                      <td className="max-w-[330px] px-3 py-2.5 leading-relaxed text-muted-foreground">
                        <div>{fdaReviewText(study.design)}</div>
                        <div className="mt-1">
                          {fdaReviewText(study.population, "人群未自动确认")}
                          {study.sample_size ? `；N=${String(study.sample_size)}` : ""}
                        </div>
                      </td>
                      <td className="max-w-[190px] px-3 py-2.5 text-muted-foreground">
                        {fdaReviewText(study.dosing, "需查阅原文")}
                      </td>
                      <td className="max-w-[360px] px-3 py-2.5 leading-relaxed text-muted-foreground">
                        {pkParameters.length > 0 && (
                          <div className="flex flex-wrap gap-x-2 gap-y-1 text-foreground">
                            {pkParameters.map((symbol) => <FdaPkSymbol key={symbol} symbol={symbol} />)}
                          </div>
                        )}
                        {findings.slice(0, 2).map((finding) => <p key={finding} className="mt-1">{finding}</p>)}
                      </td>
                      <td className="px-3 py-2.5 text-right whitespace-nowrap">
                        <FdaReviewEvidenceLink url={source.url} page={firstPage} label={pageLabel} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {parameters.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-xs font-semibold text-foreground">关键 PK 参数证据</h4>
          <div className="overflow-x-auto rounded-md border border-border">
            <table className="min-w-[920px] w-full text-xs">
              <thead className="bg-muted/30 text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-medium">参数</th>
                  <th className="px-3 py-2 text-left font-medium">数值 / 统计口径</th>
                  <th className="px-3 py-2 text-left font-medium">原文证据</th>
                  <th className="px-3 py-2 text-right font-medium">页码</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/70">
                {parameters.map((parameter, index) => (
                  <tr key={`${String(parameter.symbol || "parameter")}-${String(parameter.page || index)}-${index}`} className="align-top hover:bg-muted/20">
                    <td className="px-3 py-2.5">
                      <FdaPkSymbol symbol={fdaReviewText(parameter.symbol, "PK")} />
                      <div className="mt-1 text-[11px] text-muted-foreground">{fdaReviewText(parameter.display_name)}</div>
                    </td>
                    <td className="px-3 py-2.5 font-medium text-foreground">
                      <div>{parameter.value ? `${String(parameter.value)}${parameter.unit ? ` ${String(parameter.unit)}` : ""}` : "数值待原文复核"}</div>
                      {(parameter.statistic || parameter.condition) && (
                        <div className="mt-1 text-[11px] font-normal text-muted-foreground">
                          {[parameter.statistic, parameter.condition].filter(Boolean).map(String).join("；")}
                        </div>
                      )}
                    </td>
                    <td className="max-w-[540px] px-3 py-2.5 leading-relaxed text-muted-foreground">
                      {fdaReviewText(parameter.evidence)}
                    </td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <FdaReviewEvidenceLink url={parameter.source_url} page={parameter.page} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {topicRows.length > 0 && (
        <div className="mt-4">
          <h4 className="mb-2 text-xs font-semibold text-foreground">影响方案设计的临床药理结论</h4>
          <div className="divide-y divide-border rounded-md border border-border">
            {topicRows.map((topic, index) => (
              <div key={`${topic.label}-${String(topic.page || index)}-${index}`} className="grid gap-2 px-3 py-2.5 text-xs sm:grid-cols-[190px_1fr_auto]">
                <strong className="text-foreground">{topic.label}</strong>
                <span className="leading-relaxed text-muted-foreground">{fdaReviewText(topic.summary)}</span>
                <span className="sm:text-right"><FdaReviewEvidenceLink url={topic.source_url} page={topic.page} /></span>
              </div>
            ))}
          </div>
        </div>
      )}

      {(gaps.length > 0 || managerNotes.length > 0) && (
        <div className="mt-4 grid gap-4 border-t border-border pt-3 md:grid-cols-2">
          {gaps.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-amber-700 dark:text-amber-300">证据缺口</h4>
              <ul className="mt-2 space-y-1 text-xs leading-relaxed text-muted-foreground">
                {gaps.map((gap) => <li key={gap}>• {gap}</li>)}
              </ul>
            </div>
          )}
          {managerNotes.length > 0 && (
            <div>
              <h4 className="text-xs font-semibold text-foreground">项目经理复核要点</h4>
              <ul className="mt-2 space-y-1 text-xs leading-relaxed text-muted-foreground">
                {managerNotes.map((note) => <li key={note}>• {note}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </section>
  );
}

export function EmaProductsSummary({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const professionalProducts = recordArray(data.professional_products);
  const rawProducts = recordArray(data.products);
  const products = professionalProducts.length > 0 ? professionalProducts : rawProducts;
  if (products.length === 0) {
    return (
      <p className="mt-3 text-sm text-muted-foreground">
        暂无可展示的EMA中央审批产品。
      </p>
    );
  }

  const documentSummary =
    data.document_summary &&
    typeof data.document_summary === "object" &&
    !Array.isArray(data.document_summary)
      ? (data.document_summary as Record<string, unknown>)
      : {};
  const verifiedPdfCount = Number(documentSummary.verified_pdf || 0);
  const fallbackPageCount = Number(documentSummary.fallback_page || 0);
  const clinicalPkEvidence = fdaReviewRecord(data.clinical_pk_evidence);
  const professionalNote =
    String(data.professional_note || "").trim() ||
    "EMA 中央审批信息用于核对欧盟批准状态、SmPC/PL、EPAR Assessment Report 和持证商信息；临床试验设计时请以官方文件最新版为准。";
  const totalCount = String(data.total_count ?? (rawProducts.length || products.length));

  return (
    <div className="mt-3">
      <div className="border-b border-border pb-3 text-xs text-muted-foreground">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
          <span>
            找到{" "}
            <strong className="text-foreground">
              {totalCount}
            </strong>{" "}
            个 EMA 中央审批产品
          </span>
          {(verifiedPdfCount > 0 || fallbackPageCount > 0) && (
            <span>
              文件链接：已验证 PDF {verifiedPdfCount}，需到 EMA 主页核对 {fallbackPageCount}
            </span>
          )}
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded-md border border-border">
        <table className="min-w-[1120px] w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
              <th className="w-12 px-3 py-2.5 font-medium">序号</th>
              <th className="px-3 py-2.5 font-medium">商品名 / EMA 编号</th>
              <th className="px-3 py-2.5 font-medium">药物名称 / 活性成分</th>
              <th className="px-3 py-2.5 font-medium">规格</th>
              <th className="px-3 py-2.5 font-medium">剂型</th>
              <th className="px-3 py-2.5 font-medium">持证商 / 状态</th>
              <th className="px-3 py-2.5 font-medium">关键官方文件</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border/70">
            {products.map((product, index) => {
              const fallbackDocuments = [
                {
                  type: "product_information",
                  label: "Product Information (SmPC/PL)",
                  url:
                    product.product_information_local_url ||
                    product.product_information_url ||
                    product.smpc_pdf_url,
                  availability: product.product_information_local_url
                    ? "verified_pdf"
                    : "",
                },
                {
                  type: "assessment_report",
                  label: "Assessment Report",
                  url:
                    product.assessment_report_local_url ||
                    product.assessment_report_url,
                  availability: product.assessment_report_local_url
                    ? "verified_pdf"
                    : "",
                },
                {
                  type: "epar",
                  label: "EMA 药品主页",
                  url: product.epar_url,
                  availability: "verified_page",
                },
              ];
              const visibleDocuments = (
                recordArray(product.documents).length > 0
                  ? recordArray(product.documents)
                  : fallbackDocuments
              )
                .map((document) => {
                  const type = String(document.type || "");
                  const availability = String(document.availability || "");
                  const url = String(
                    document.open_url ||
                      document.local_url ||
                      document.url ||
                      "",
                  ).trim();
                  let label = String(document.label || "EMA 官方文件");
                  if (availability === "fallback_page") {
                    label =
                      type === "product_information"
                        ? "SmPC/PL（EMA主页核对）"
                        : type === "assessment_report"
                          ? "Assessment Report（EMA主页核对）"
                          : "EMA 药品主页";
                  } else if (type === "product_information") {
                    label = "Product Information (SmPC/PL)";
                  } else if (type === "assessment_report") {
                    label = "Assessment Report";
                  } else if (type === "epar") {
                    label = "EMA 药品主页";
                  }
                  return { ...document, availability, label, type, url };
                })
                .filter((document) => document.url);
              const specification =
                String(product.specification || "").trim() ||
                textList(product.strengths) ||
                "EMA公开清单未单列，请在 SmPC/PL 中核对";
              const dosageForm =
                String(product.dosage_form || "").trim() ||
                textList(product.dosage_forms) ||
                "-";
              const brandName = String(
                product.brand_name || product.product_name || "EMA 产品",
              );
              const activeName = String(
                product.generic_name ||
                  product.active_substance ||
                  "活性成分未列出",
              );

              return (
                <tr
                  key={String(
                    product.ema_product_number ||
                      product.product_name ||
                      index,
                  )}
                  className="hover:bg-muted/20"
                >
                  <td className="px-3 py-3 text-muted-foreground">
                    {String(product.index || index + 1)}
                  </td>
                  <td className="max-w-[230px] px-3 py-3 font-medium text-foreground">
                    <span className="break-words">{brandName}</span>
                    {product.ema_product_number && (
                      <span className="mt-1 block text-xs font-normal text-muted-foreground">
                        {String(product.ema_product_number)}
                      </span>
                    )}
                    {product.authorization_date && (
                      <span className="mt-1 block text-xs font-normal text-muted-foreground">
                        授权日期：{String(product.authorization_date)}
                      </span>
                    )}
                  </td>
                  <td className="max-w-[230px] px-3 py-3 text-muted-foreground">
                    <span className="break-words">{activeName}</span>
                    {product.atc_code && (
                      <span className="mt-1 block text-xs">
                        ATC：{String(product.atc_code)}
                      </span>
                    )}
                  </td>
                  <td className="max-w-[220px] px-3 py-3 font-mono text-xs text-foreground">
                    <span className="break-words">{specification}</span>
                  </td>
                  <td className="max-w-[180px] px-3 py-3 text-muted-foreground">
                    <span className="break-words">{dosageForm}</span>
                  </td>
                  <td className="max-w-[230px] px-3 py-3 text-muted-foreground">
                    <span className="break-words">
                      {String(product.holder || "-")}
                    </span>
                    {product.medicine_status && (
                      <span className="mt-2 inline-flex rounded-sm bg-emerald-500/10 px-2 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
                        {String(product.medicine_status)}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    {visibleDocuments.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {visibleDocuments.map((document, documentIndex) => (
                          <div
                            key={`${document.url}-${documentIndex}`}
                            className="flex flex-wrap items-center gap-2"
                          >
                            <OfficialDocumentLink
                              href={document.url}
                              label={String(document.label)}
                            />
                            {document.availability === "fallback_page" && (
                              <span className="text-[11px] text-amber-700 dark:text-amber-300">
                                直接 PDF 未验证，进入 EMA 主页后在 Documents 栏核对
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <span className="text-muted-foreground">-</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {Object.keys(clinicalPkEvidence).length > 0 && (
        <div className="mt-4">
          <EmaClinicalPkEvidence evidence={clinicalPkEvidence} />
        </div>
      )}

      <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
        {professionalNote}
      </p>
    </div>
  );
}

export function FdaPsgSummary({
  data,
}: {
  data: Record<string, unknown>;
}) {
  const summary =
    data.guidance_summary &&
    typeof data.guidance_summary === "object" &&
    !Array.isArray(data.guidance_summary)
      ? (data.guidance_summary as Record<string, unknown>)
      : data;
  const records = recordArray(data.psg_records);
  const documents = recordArray(data.guidance_documents);
  const fallbackUrls = Array.isArray(data.psg_official_urls)
    ? data.psg_official_urls
    : [data.psg_url];
  const visibleDocuments =
    documents.length > 0
      ? documents
      : fallbackUrls
          .filter(Boolean)
          .map((url) => ({
            label: "FDA Product-Specific Guidance",
            url,
          }));
  const recommendedStudies =
    summary.recommended_studies || summary.be_study_type;

  return (
    <div className="mt-3">
      <dl className="grid gap-x-6 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
        <SummaryField
          label="有效成分"
          value={summary.active_ingredient || data.drug_name}
          emphasize
        />
        <SummaryField label="剂型" value={summary.dosage_form} />
        <SummaryField label="给药途径" value={summary.route} />
        <SummaryField
          label="推荐BE研究"
          value={recommendedStudies || "请查看FDA指导原则PDF"}
          emphasize
        />
        <SummaryField label="研究设计" value={summary.study_design} />
        <SummaryField label="参比制剂（RLD）" value={summary.rld_name} />
        <SummaryField label="样本量提示" value={summary.sample_size} />
        <SummaryField
          label="分析方法"
          value={summary.analytical_method}
        />
        <SummaryField label="主要PK指标" value={summary.pk_parameters} />
      </dl>

      {visibleDocuments.length > 0 && (
        <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
          {visibleDocuments.map((document, index) => (
            <OfficialDocumentLink
              key={`${String(document.url)}-${index}`}
              href={String(document.url || "")}
              label={String(document.label || "打开FDA PSG PDF")}
            />
          ))}
        </div>
      )}

      {records.length > 1 && (
        <div className="mt-4 border-t border-border pt-3">
          <h4 className="text-xs font-medium text-muted-foreground">
            匹配到的指导原则版本
          </h4>
          <div className="mt-2 divide-y divide-border">
            {records.map((record, index) => (
              <div
                key={String(record.psg_url || index)}
                className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0">
                  <p className="break-words text-sm font-medium text-foreground">
                    {uniqueJoined([
                      record.active_ingredient,
                      record.dosage_form,
                      record.route,
                    ]) || `指导原则 ${index + 1}`}
                  </p>
                  <p className="mt-1 break-words text-xs text-muted-foreground">
                    {uniqueJoined([
                      record.recommended_studies,
                      record.guidance_status,
                      record.guidance_date,
                    ])}
                  </p>
                </div>
                {record.psg_url && (
                  <a
                    href={String(record.psg_url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex flex-none items-center gap-1.5 text-xs font-medium text-primary hover:underline"
                  >
                    <FileText className="h-4 w-4" aria-hidden="true" />
                    打开PDF
                    <ExternalLink
                      className="h-3.5 w-3.5"
                      aria-hidden="true"
                    />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CdeTrialsSummary({
  data,
  showSourceLink = true,
}: {
  data: Record<string, unknown>;
  showSourceLink?: boolean;
}) {
  const generatedTable = buildCdeTrialsMarkdownTable(data);
  const markdownTable =
    typeof data.markdown_table === "string" && data.markdown_table.includes("|")
      ? data.markdown_table
      : generatedTable;
  const total = data.source_total_count ?? data.total_count ?? data.trial_count;
  const detailed = data.fetched_detail_count ?? data.trial_count;

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-3 text-xs">
        {total != null && (
          <span>
            <span className="text-muted-foreground">匹配记录 </span>
            <strong className="text-foreground">{String(total)}</strong>
          </span>
        )}
        {detailed != null && String(detailed) !== String(total) && (
          <span>
            <span className="text-muted-foreground">本次详情 </span>
            <strong className="text-foreground">{String(detailed)}</strong>
          </span>
        )}
      </div>
      {markdownTable ? (
        <MarkdownTableRenderer content={markdownTable} />
      ) : (
        <p className="py-4 text-sm text-muted-foreground">暂无可展示的临床试验表格。</p>
      )}
      {data.note && (
        <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
          {String(data.note)}
        </p>
      )}
      {showSourceLink && data.search_url && (
        <LinkChip href={String(data.search_url)}>打开 CDE 临床试验公示查询页</LinkChip>
      )}
    </div>
  );
}

function PubMedSummary({ data }: { data: Record<string, unknown> }) {
  const articles = recordArray(data.articles);
  const evidenceCategories = recordArray(data.evidence_categories);
  const groupedCategories = evidenceCategories.filter(
    (category) => recordArray(category.articles).length > 0,
  );
  const categoryCounts =
    data.category_counts &&
    typeof data.category_counts === "object" &&
    !Array.isArray(data.category_counts)
      ? Object.entries(data.category_counts as Record<string, unknown>)
      : [];

  const renderArticleLinks = (article: Record<string, unknown>) => {
    const doi = String(article.doi || "").trim();
    const doiUrl = String(article.doi_url || (doi ? `https://doi.org/${doi}` : "")).trim();
    const fullTextUrl = String(article.full_text_url || "").trim();
    return (
      <div className="flex flex-wrap gap-2">
        {article.url && (
          <OfficialDocumentLink href={String(article.url)} label="PubMed" />
        )}
        {doiUrl && (
          <OfficialDocumentLink href={doiUrl} label="DOI" />
        )}
        {fullTextUrl && fullTextUrl !== String(article.url || "") && fullTextUrl !== doiUrl && (
          <OfficialDocumentLink href={fullTextUrl} label="原文" />
        )}
      </div>
    );
  };

  const renderArticlesTable = (tableArticles: Record<string, unknown>[]) => (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="min-w-full text-left text-xs">
        <thead className="bg-muted/40 text-muted-foreground">
          <tr>
            <th className="w-28 px-3 py-2 font-semibold">PMID</th>
            <th className="min-w-[26rem] px-3 py-2 font-semibold">标题</th>
            <th className="w-56 px-3 py-2 font-semibold">期刊 / 年份</th>
            <th className="w-48 px-3 py-2 font-semibold">研究类型</th>
            <th className="w-36 px-3 py-2 font-semibold">摘要相关性</th>
            <th className="w-40 px-3 py-2 font-semibold">链接</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {tableArticles.map((article, index) => {
            const pmid = String(article.pmid || "");
            const matchedTerms = Array.isArray(article.matched_terms)
              ? article.matched_terms.map((term) => String(term)).filter(Boolean)
              : [];
            const abstract = String(article.abstract || "");
            const relevance = article.abstract_relevance
              ? `${String(article.abstract_relevance_label || "")} ${String(article.abstract_relevance)}/100`
              : String(article.abstract_relevance_label || "—");
            return (
              <tr key={`${pmid || "pubmed"}-${index}`} className="align-top">
                <td className="px-3 py-3 font-mono text-[0.72rem] text-muted-foreground">
                  {pmid || "—"}
                </td>
                <td className="px-3 py-3">
                  <div className="font-semibold leading-relaxed text-foreground">
                    {String(article.title || "Untitled PubMed record")}
                  </div>
                  {abstract && (
                    <div className="mt-1 line-clamp-3 text-muted-foreground">
                      {abstract}
                    </div>
                  )}
                  {matchedTerms.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {matchedTerms.slice(0, 5).map((term) => (
                        <span key={term} className="badge badge-info">{term}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td className="px-3 py-3 text-muted-foreground">
                  <div className="font-medium text-foreground">{String(article.journal || "—")}</div>
                  <div>{String(article.year || "—")}</div>
                </td>
                <td className="px-3 py-3 text-foreground">
                  {String(article.study_type || article.category || "药品相关文献")}
                </td>
                <td className="px-3 py-3">
                  <span className="rounded-md bg-muted px-2 py-1 text-foreground">
                    {relevance || "—"}
                  </span>
                </td>
                <td className="px-3 py-3">
                  {renderArticleLinks(article)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-3 text-xs">
        <span>
          <span className="text-muted-foreground">文献数 </span>
          <strong className="text-foreground">{String(data.count ?? articles.length)}</strong>
        </span>
        {data.search_strategy && (
          <span>
            <span className="text-muted-foreground">命中策略 </span>
            <strong className="text-foreground">{String(data.search_strategy)}</strong>
          </span>
        )}
        {categoryCounts.length > 0 && (
          <span className="text-muted-foreground">
            {categoryCounts.map(([label, count]) => `${label} ${String(count)}`).join(" · ")}
          </span>
        )}
      </div>

      {data.searched_aliases && (
        <p className="mt-2 text-xs text-muted-foreground">
          检索名称：{String(data.searched_aliases)}
        </p>
      )}

      {groupedCategories.length > 0 ? (
        <div className="mt-3 space-y-4">
          {groupedCategories.map((category) => {
            const categoryArticles = recordArray(category.articles);
            return (
              <section key={String(category.id || category.label)} className="space-y-2">
                <div className="flex flex-wrap items-end justify-between gap-2">
                  <div>
                    <h4 className="text-sm font-semibold text-foreground">
                      {String(category.label || "药品相关文献")}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {String(category.count ?? categoryArticles.length)} 篇
                      </span>
                    </h4>
                    {category.description && (
                      <p className="mt-1 text-xs text-muted-foreground">
                        {String(category.description)}
                      </p>
                    )}
                  </div>
                </div>
                {renderArticlesTable(categoryArticles)}
              </section>
            );
          })}
        </div>
      ) : articles.length > 0 ? (
        <div className="mt-3">
          {renderArticlesTable(articles)}
        </div>
      ) : (
        <p className="py-4 text-sm text-muted-foreground">
          暂无可展示的 PubMed 文献。{data.note ? String(data.note) : ""}
        </p>
      )}
    </div>
  );
}

function MolecularFormula({ formula }: { formula: string }) {
  const tokens = Array.from(formula.matchAll(/([A-Z][a-z]?)(\d*)/g));
  if (tokens.length === 0) {
    return <span>{formula || "—"}</span>;
  }

  return (
    <span className="font-mono text-base font-semibold tracking-normal text-primary">
      {tokens.map((token, index) => (
        <span key={`${token[1]}-${index}`}>
          {token[1]}
          {token[2] && <sub className="text-[0.68em]">{token[2]}</sub>}
        </span>
      ))}
    </span>
  );
}

function PubChemSummary({ data }: { data: Record<string, unknown> }) {
  const compounds = recordArray(data.compounds);
  const isCombination = Boolean(data.is_combination);

  if (compounds.length === 0) {
    return (
      <div className="mt-3 rounded-md border border-border bg-muted/20 p-3 text-sm text-muted-foreground">
        PubChem 暂未返回可展示的分子结构记录。
      </div>
    );
  }

  return (
    <div className="mt-3 space-y-4">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 border-b border-border pb-3 text-xs">
        <span>
          <span className="text-muted-foreground">匹配成分 </span>
          <strong className="text-foreground">{String(data.compound_count ?? compounds.length)}</strong>
        </span>
        <span>
          <span className="text-muted-foreground">查询类型 </span>
          <strong className="text-foreground">{isCombination ? "复方，按活性成分拆分" : "单方"}</strong>
        </span>
        {data.normalized_query && (
          <span className="min-w-0">
            <span className="text-muted-foreground">标准化名称 </span>
            <strong className="break-words text-foreground">{String(data.normalized_query)}</strong>
          </span>
        )}
      </div>

      <div className="space-y-4">
        {compounds.map((compound, index) => {
          const formula = String(compound.molecular_formula || "");
          const analysis =
            compound.structure_analysis && typeof compound.structure_analysis === "object"
              ? (compound.structure_analysis as Record<string, unknown>)
              : {};
          const groups = recordArray(analysis.functional_groups);
          const formulaParts = recordArray(compound.formula_parts);
          const modelLinks = recordArray(compound.model_links);
          const imageUrl = String(compound.structure_2d_image || compound.structure_thumbnail || "");

          return (
            <article
              key={String(compound.cid || compound.query_component || index)}
              className="rounded-md border border-border bg-card p-4"
            >
              <div className="grid gap-4 lg:grid-cols-[minmax(14rem,18rem)_1fr]">
                <div className="rounded-md border border-border bg-white p-3 dark:bg-white">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={`${String(compound.query_component || "compound")} molecular structure`}
                      className="mx-auto aspect-square max-h-64 w-full object-contain"
                      loading="lazy"
                    />
                  ) : (
                    <div className="flex aspect-square items-center justify-center text-sm text-muted-foreground">
                      No structure image
                    </div>
                  )}
                </div>

                <div className="min-w-0">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="break-words text-base font-semibold text-foreground">
                        {String(compound.query_component || `Compound ${index + 1}`)}
                      </h4>
                      <p className="mt-1 text-xs text-muted-foreground">
                        PubChem CID {String(compound.cid || "—")}
                      </p>
                    </div>
                    {formula && <MolecularFormula formula={formula} />}
                  </div>

                  <div className="mt-3 grid gap-x-5 gap-y-2 sm:grid-cols-2">
                    <SummaryField label="分子量" value={compound.molecular_weight ? `${compound.molecular_weight} g/mol` : ""} emphasize />
                    <SummaryField label="IUPAC 名称" value={compound.iupac_name} />
                    <SummaryField label="InChIKey" value={compound.inchi_key} />
                    <SummaryField label="SMILES" value={compound.isomeric_smiles || compound.canonical_smiles} />
                    <SummaryField label="XLogP" value={compound.xlogp} />
                    <SummaryField label="TPSA" value={compound.tpsa ? `${compound.tpsa} A^2` : ""} />
                    <SummaryField label="氢键供体/受体" value={`${compound.h_bond_donor_count ?? "—"} / ${compound.h_bond_acceptor_count ?? "—"}`} />
                    <SummaryField label="可旋转键" value={compound.rotatable_bond_count} />
                  </div>

                  {formulaParts.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">元素组成</h5>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {formulaParts.map((part, partIndex) => (
                          <span
                            key={`${String(part.element)}-${partIndex}`}
                            className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/30 px-2 py-1 text-xs text-foreground"
                          >
                            <strong>{String(part.element)}</strong>
                            <span>{String(part.name_cn || part.name_en || "")}</span>
                            <span className="text-muted-foreground">x {String(part.count || 1)}</span>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {groups.length > 0 && (
                    <div className="mt-4">
                      <h5 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">结构片段解析</h5>
                      <div className="mt-2 grid gap-2 sm:grid-cols-2">
                        {groups.map((group, groupIndex) => (
                          <div
                            key={`${String(group.key || group.name_en)}-${groupIndex}`}
                            className="rounded-md border border-border bg-muted/20 px-3 py-2"
                          >
                            <div className="text-sm font-medium text-foreground">
                              {String(group.name_cn || group.name_en || "结构片段")}
                            </div>
                            <div className="mt-1 text-xs leading-relaxed text-muted-foreground">
                              {String(group.description || "")}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3">
                    {modelLinks.map((link, linkIndex) => (
                      <a
                        key={`${String(link.label || linkIndex)}-${linkIndex}`}
                        href={String(link.url || "")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex min-h-8 items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-foreground transition-colors hover:border-primary/50 hover:bg-primary/5 hover:text-primary"
                      >
                        {String(link.label || "Open model")}
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>

      {data.data_source_note && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {String(data.data_source_note)}
        </p>
      )}
    </div>
  );
}

function ThemeToggle() {
  const { theme, toggleTheme } = useTheme();
  return (
    <button
      onClick={toggleTheme}
      className="p-2 rounded-md hover:bg-muted transition-colors"
      title={theme === "light" ? "切换到深色模式" : "切换到浅色模式"}
    >
      {theme === "light" ? (
        <svg className="w-5 h-5 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
        </svg>
      ) : (
        <svg className="w-5 h-5 text-warning" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )}
    </button>
  );
}

const QualityScoreRing = memo(({ score }: { score: number }) => {
  const color = score >= 90 ? "#22c55e" : score >= 80 ? "#eab308" : "#ef4444";
  const circumference = 2 * Math.PI * 20;
  const offset = circumference - (score / 100) * circumference;
  const label = score >= 90 ? "优秀" : score >= 80 ? "良好" : "需改进";

  return (
    <div className="flex items-center gap-3">
      <div className="relative w-14 h-14">
        <svg viewBox="0 0 50 50" className="w-full h-full -rotate-90">
          <circle cx="25" cy="25" r="20" fill="none" stroke="hsl(var(--muted))" strokeWidth="4" />
          <circle cx="25" cy="25" r="20" fill="none" stroke={color} strokeWidth="4"
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s ease-out" }} />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-bold" style={{ color }}>{score}</span>
        </div>
      </div>
      <div>
        <div className="text-sm font-bold text-foreground">{label}</div>
        <div className="text-xs text-muted-foreground">质量评分</div>
      </div>
    </div>
  );
});

export const DatasourcePanel = memo(({
  id,
  name,
  icon,
  status,
  duration,
  data,
  drugName,
  isExpanded,
  onToggle,
}: {
  id: string;
  name: string;
  icon: React.ReactNode;
  status: string;
  duration?: number;
  data?: Record<string, unknown>;
  drugName?: string;
  isExpanded: boolean;
  onToggle: () => void;
}) => {
  const isSuccess = status === "success";
  const isError = status === "error";
  const isNotFound = status === "not_found";
  const isProblem = isError || isNotFound;
  const isFdaReview = id === "fda_review";
  const isFda = id === "fda";
  const isFdaGuid = id === "fda_guid";
  const isEma = id === "ema";
  const isCdeTrials = id === "cde_trials";
  const isCdeRef = id === "cde_reflisted";
  const isPmda = id === "pmda";
  const isHealthCanada = id === "health_canada";
  const isPubMed = id === "pubmed";
  const isDailyMed = id === "dailymed";
  const isPubChem = id === "pubchem";
  const sourceUrl = getSourceUrl(id, data);

  const localFilePath = drugName ? `sources/${id}/${drugName}.json` : null;

  const borderClass = isSuccess ? "border-l-success" : isProblem ? "border-l-destructive" : "border-l-muted-foreground/30";

  const handleOpenSource = useCallback(() => {
    if (!sourceUrl) return;
    window.open(sourceUrl, "_blank", "noopener,noreferrer");
  }, [sourceUrl]);

  const handleCopy = useCallback(async () => {
    if (!data) return;
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
  }, [data]);

  const handleExportCsv = useCallback(() => {
    if (!data) return;

    const blob = new Blob(["\uFEFF", buildCsv(data)], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${safeDownloadName(`${drugName || "query"}_${id}`)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  }, [data, drugName, id]);

  return (
    <div className={`card overflow-hidden border-l-4 ${borderClass} transition-all duration-200`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-3">
          <span className="text-lg" aria-hidden="true">{icon}</span>
          <span className="font-medium text-sm text-foreground">{name}</span>
          {isSuccess && <span className="badge badge-success">✓ 成功</span>}
          {isError && <span className="badge badge-danger">✗ 失败</span>}
          {isNotFound && <span className="badge badge-warning">∅ 无数据</span>}
          {duration !== undefined && (
            <span className="text-xs text-muted-foreground">{duration.toFixed(1)}s</span>
          )}
        </div>
        <span className="text-xs text-muted-foreground flex items-center gap-1">
          {isExpanded ? "收起 ▲" : "展开 ▼"}
        </span>
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 animate-fadeIn border-t border-border">
          {data && Object.keys(data).length > 0 ? (
            isFdaReview ? (
              <FdaReviewDocuments review={data} />
            ) : isFda ? (
              <FdaSummary data={data} showSourceLink={false} />
            ) : isFdaGuid ? (
              <FdaPsgSummary data={data} />
            ) : isEma ? (
              <EmaProductsSummary data={data} />
            ) : isCdeTrials ? (
              <CdeTrialsSummary data={data} showSourceLink={false} />
            ) : isCdeRef ? (
              <CdeReferenceSummary data={data} />
            ) : isPmda ? (
              <PmdaProductsSummary data={data} />
            ) : isHealthCanada ? (
              <HealthCanadaProductsSummary data={data} />
            ) : isPubMed ? (
              <PubMedSummary data={data} />
            ) : isDailyMed ? (
              <DailyMedSummary data={data} />
            ) : isPubChem ? (
              <PubChemSummary data={data} />
            ) : (
              <div className="mt-3 space-y-0">
                {Object.entries(data).map(([key, value]) => {
                  const displayLabel = key.replace(/_/g, ' ');
                  if (key === "reference_drugs" && Array.isArray(value) && value.length > 0) {
                    return (
                      <div key={key} className="data-row group">
                        <span className="data-label">{displayLabel}</span>
                        <ReferenceDrugTable drugs={value as Record<string, unknown>[]} />
                      </div>
                    );
                  }
                  if (key === "markdown_table" && typeof value === "string" && value.includes("|")) {
                    return (
                      <div key={key} className="data-row group">
                        <span className="data-label">{displayLabel}</span>
                        <MarkdownTableRenderer content={value} />
                      </div>
                    );
                  }
                  const formatted = formatDataForDisplay(value);
                  if (key === "reference_drugs" || key === "markdown_table") return null;
                  if (formatted && formatted.length > 500 && !formatted.includes("\n")) {
                    return (
                      <details key={key} className="data-row group cursor-pointer">
                        <summary className="flex items-center gap-1 hover:text-primary transition-colors">
                          <span className="data-label">{displayLabel}</span>
                          <span className="text-xs text-muted-foreground">({(formatted.length / 1024).toFixed(1)} KB, 点击展开)</span>
                        </summary>
                        <pre className="mt-2 p-3 bg-muted/30 rounded text-xs overflow-x-auto whitespace-pre-wrap break-all max-h-[300px] overflow-y-auto">{formatted}</pre>
                      </details>
                    );
                  }
                  return <DataRow key={key} label={displayLabel} value={formatted || "—"} />;
                })}
              </div>
            )
          ) : isError ? (
            <div className="mt-3 p-3 bg-destructive/5 rounded text-sm text-destructive">
              <p>错误详情: 数据源返回异常</p>
              <p className="text-xs mt-1 text-muted-foreground">建议: 稍后重试或检查网络连接</p>
            </div>
          ) : isNotFound ? (
            <div className="mt-3 p-3 bg-warning/5 rounded text-sm text-warning">
              该数据源未找到相关数据，可能药品未在该地区上市或数据尚未收录。
            </div>
          ) : (
            <div className="mt-3 p-3 text-sm text-muted-foreground">暂无详细数据</div>
          )}

          {localFilePath && isSuccess && !isFdaReview && !isFda && !isFdaGuid && !isEma && !isCdeTrials && !isCdeRef && !isPmda && !isHealthCanada && !isPubMed && !isDailyMed && !isPubChem && (
            <div className="mt-3 pt-3 border-t border-border">
              <div className="local-file-info flex items-center gap-2 px-3 py-2 bg-emerald-500/5 rounded-md text-xs">
                <span>📁</span>
                <code className="text-emerald-700 dark:text-emerald-400 font-mono break-all">{localFilePath}</code>
                <span className="text-muted-foreground ml-auto">已保存至本地</span>
              </div>
            </div>
          )}

          <div className="flex gap-2 mt-3 pt-3 border-t border-border">
            <button
              type="button"
              className="btn btn-ghost text-xs h-7 gap-1 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={handleOpenSource}
              disabled={!sourceUrl}
              title={sourceUrl ? "打开该数据源的官方原始页面" : "该数据源未返回原文链接"}
            >
              🔗 {isFdaReview ? "查看 FDA 申请页" : "查看原文"}
            </button>
            {!isFdaReview && (
              <button
                type="button"
                className="btn btn-ghost text-xs h-7 gap-1 disabled:cursor-not-allowed disabled:opacity-40"
                onClick={handleCopy}
                disabled={!data}
              >
                📋 复制
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost text-xs h-7 gap-1 disabled:cursor-not-allowed disabled:opacity-40"
              onClick={handleExportCsv}
              disabled={!data}
              title="导出当前数据源的查询结果"
            >
              📥 导出CSV
            </button>
          </div>
        </div>
      )}
    </div>
  );
});

const DATASOURCE_KEY_MAP: Record<string, string> = {
  fda: "fda",
  fda_guid: "fda_guid",
  fda_review: "fda_review",
  ema: "ema",
  ich_m13a: "ich_m13a",
  health_canada: "health_canada",
  pubmed: "pubmed",
  pubchem: "pubchem",
  cde_reflisted: "cde_reflisted",
  cde_trials: "cde_trials",
  nmpa: "nmpa",
  cde_yzxpj: "cde_yzxpj",
  dailymed: "dailymed",
};

const ResultRenderer = memo(({ result, selectedDatasources }: { result: NonNullable<TaskStatus["result"]>; selectedDatasources?: string[] }) => {
  const d = result.data || {};
  const fda   = d.fda   as Record<string, unknown> || {};
  const fdag  = d.fda_guid as Record<string, unknown> || {};
  const ema   = d.ema   as Record<string, unknown> || {};
  const pmda  = d.pmda  as Record<string, unknown> || {};
  const healthCanada = d.health_canada as Record<string, unknown> || {};
  const ich   = d.ich_m13a as Record<string, unknown> || {};
  const pub   = d.pubmed as Record<string, unknown> || {};
  const pubchem = d.pubchem as Record<string, unknown> || {};
  const cdeRef    = d.cde_reflisted as Record<string, unknown> || {};
  const cdeTrials = d.cde_trials as Record<string, unknown> || {};
  const nmpa      = d.nmpa as Record<string, unknown> || {};
  const cdeYzxpj  = d.cde_yzxpj as Record<string, unknown> || {};
  const dailymed  = d.dailymed as Record<string, unknown> || {};

  const isSelected = (dsId: string) => !selectedDatasources || selectedDatasources.length === 0 || selectedDatasources.includes(dsId);

  return (
    <div className="space-y-3">
      {isSelected("fda") && (
      <SectionCard title="FDA 药品信息" icon="🇺🇸" data={fda}>
        <FdaSummary data={fda} />
      </SectionCard>
      )}

      {isSelected("fda_guid") && (
      <SectionCard title="FDA 生物等效性指南（PSG）" icon="📋" data={fdag}>
        <FdaPsgSummary data={fdag} />
      </SectionCard>
      )}

      {isSelected("ema") && (
      <SectionCard title="EMA 欧洲药品管理局" icon="🇪🇺" data={ema}>
        <EmaProductsSummary data={ema} />
      </SectionCard>
      )}

      {isSelected("pmda") && pmda && Object.keys(pmda).length > 0 && (
      <SectionCard title="PMDA 日本药品医疗器械综合机构" icon="JP" data={pmda}>
        <PmdaProductsSummary data={pmda} />
      </SectionCard>
      )}

      {isSelected("health_canada") && healthCanada && Object.keys(healthCanada).length > 0 && (
      <SectionCard title="Health Canada DPD" icon="CA" data={healthCanada}>
        <HealthCanadaProductsSummary data={healthCanada} />
      </SectionCard>
      )}

      {isSelected("ich_m13a") && (
      <SectionCard title="ICH M13A 低风险豁免" icon="🏆" data={ich}>
        <DataRow
          label="判断"
          value={ich.is_low_risk === true ? "✅ 低风险" : ich.is_low_risk === false ? "❌ 非低风险" : "⚠️ 需确认"}
          highlight={ich.is_low_risk === true}
        />
        <DataRow label="BCS 分类" value={String(ich.bcs_class || "—")} />
      </SectionCard>
      )}

      {isSelected("pubmed") && (
      <SectionCard title="PubMed 文献" icon="📚" data={pub}>
        <PubMedSummary data={pub} />
      </SectionCard>
      )}

      {isSelected("pubchem") && pubchem && Object.keys(pubchem).length > 0 && (
      <SectionCard title="PubChem 分子结构" icon={<Atom className="h-4 w-4" aria-hidden="true" />} data={pubchem}>
        <PubChemSummary data={pubchem} />
      </SectionCard>
      )}

      {isSelected("cde_reflisted") && cdeRef && Object.keys(cdeRef).length > 0 && (
        <SectionCard title="CDE 参比制剂目录" icon="🇨🇳" data={cdeRef}>
          {Array.isArray(cdeRef.reference_drugs) && cdeRef.reference_drugs.length > 0 ? (
            <>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className="text-sm text-muted-foreground">
                  共 <strong className="text-foreground">{String(cdeRef.total_count ?? cdeRef.reference_drugs.length)}</strong> 条记录
                  {cdeRef.exact_count != null && (
                    <span className="ml-1">（严格匹配 <span className="text-success font-medium">{String(cdeRef.exact_count)}</span> 条）</span>
                  )}
                </span>
                {cdeRef.source_status && (
                  <div className="flex gap-1.5 ml-auto">
                    {Object.entries(cdeRef.source_status).map(([sk, sv]: [string, any]) => (
                      <span key={sk} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-muted/50 text-muted-foreground">
                        <span className={`w-1.5 h-1.5 rounded-full ${sv.status === "success" ? "bg-success" : "bg-warning"}`} />
                        {sk}: {sv.count ?? 0}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <ReferenceDrugTable drugs={cdeRef.reference_drugs as Record<string, unknown>[]} />
              {cdeRef.note && (
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{String(cdeRef.note)}</p>
              )}
            </>
          ) : (
            <>
              <DataRow label="参比制剂名称" value={String(cdeRef.ref_drug_name || cdeRef.drug_name || "—")} highlight />
              <DataRow label="持证商" value={String(cdeRef.holder || "—")} />
              <DataRow label="规格" value={String(cdeRef.strength || cdeRef.specification || "—")} />
              <DataRow label="剂型" value={String(cdeRef.dosage_form || "—")} />
              <DataRow label="参考制剂类型" value={String(cdeRef.ref_type || cdeRef.source_type || "—")} />
            </>
          )}
        </SectionCard>
      )}

      {isSelected("cde_trials") && cdeTrials && Object.keys(cdeTrials).length > 0 && (
        <SectionCard title="CDE 临床试验" icon="🔬" data={cdeTrials}>
          <CdeTrialsSummary data={cdeTrials} />
        </SectionCard>
      )}

      {isSelected("nmpa") && nmpa && Object.keys(nmpa).length > 0 && (
        <SectionCard title="NMPA (国家药监局)" icon="🏥" data={nmpa}>
          <DataRow label="药品名称" value={String(nmpa.drug_name || "—")} highlight />
          <DataRow label="批准文号" value={String(nmpa.approval_number || "—")} />
          <DataRow label="生产企业" value={String(nmpa.manufacturer || "—")} />
          <DataRow label="规格" value={String(nmpa.specification || "—")} />
        </SectionCard>
      )}

      {isSelected("cde_yzxpj") && cdeYzxpj && Object.keys(cdeYzxpj).length > 0 && (
        <SectionCard title="CDE 一致性评价" icon="✅" data={cdeYzxpj}>
          <DataRow label="评价状态" value={String(cdeYzxpj.status || "—")} highlight />
          <DataRow label="受理号" value={String(cdeYzxpj.acceptance_no || "—")} />
          <DataRow label="申请人" value={String(cdeYzxpj.applicant || "—")} />
          <DataRow label="结论" value={String(cdeYzxpj.conclusion || "—")} />
        </SectionCard>
      )}

      {isSelected("dailymed") && dailymed && Object.keys(dailymed).length > 0 && (
        <SectionCard title="DailyMed 药品信息" icon="💊" data={dailymed}>
          <DailyMedSummary data={dailymed} />
        </SectionCard>
      )}
    </div>
  );
});

function SectionCard({ title, icon, children, data }: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  data?: Record<string, unknown>;
}) {
  const [expanded, setExpanded] = useState(true);
  const hasData = data && Object.keys(data).length > 0;

  return (
    <div className={`card overflow-hidden transition-all duration-300 ${hasData ? '' : 'opacity-60'}`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full card-header flex items-center justify-between hover:bg-muted/40 transition-colors cursor-pointer"
      >
        <div className="flex items-center gap-2">
          {icon && <span className="text-base">{icon}</span>}
          <span className="section-title font-normal text-foreground">{title}</span>
          {!hasData && <span className="text-xs text-muted-foreground">(无数据)</span>}
        </div>
        <svg className={`w-4 h-4 text-muted-foreground transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {expanded && (
        <div className="card-body animate-fadeIn">
          {children}
        </div>
      )}
    </div>
  );
}

function SkeletonLoader() {
  return (
    <div className="max-w-2xl mx-auto space-y-4 animate-fadeIn">
      <div className="skeleton h-32 w-full rounded-lg" />
      <div className="grid grid-cols-2 gap-4">
        <div className="skeleton h-10 w-full rounded" />
        <div className="skeleton h-10 w-full rounded" />
      </div>
      <div className="skeleton h-12 w-full rounded-lg" />
      <div className="grid grid-cols-3 gap-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="skeleton h-24 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}

function AppContent() {
  const [stage, setStage] = useState<Stage>("submit");
  const [drugInput, setDrugInput] = useState("");
  const [selectedDrug, setSelectedDrug] = useState<DrugMapping | null>(null);
  const [selectedDrugIdentity, setSelectedDrugIdentity] = useState<ExtendedDrugResult | null>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [task, setTask] = useState<TaskStatus | null>(null);
  const [serverOk, setServerOk] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 新增：全局Loading和Error组件的状态
  const [showGlobalLoading, setShowGlobalLoading] = useState(false);
  const [loadingStage, setLoadingStage] = useState<'submitting' | 'querying' | 'processing' | 'generating' | 'downloading'>('submitting');
  const [structuredError, setStructuredError] = useState<Error | string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollFailureCountRef = useRef(0);

  const [selectedDosageForm, setSelectedDosageForm] = useState<DosageForm | null>(null);
  const [specification, setSpecification] = useState("");
  const [queryStartTime, setQueryStartTime] = useState<number | null>(null);
  const [dataSourceStatuses, setDataSourceStatuses] = useState<Array<{
    id: string; name: string; icon?: string;
    status: string; message?: string; elapsed?: number;
  }>>([]);
  const [expandedCardId, setExpandedCardId] = useState<string | null>(null);
  const [availableDatasources, setAvailableDatasources] = useState<DatasourceConfig[]>([]);
  const [dsLoadError, setDsLoadError] = useState<string | null>(null);
  const [dsRetrying, setDsRetrying] = useState(false);
  const [usingFallback, setUsingFallback] = useState(false);
  const [selectedDatasources, setSelectedDatasources] = useState<string[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [resultTab, setResultTab] = useState<ResultTab>("all");
  const [expandedResultPanels, setExpandedResultPanels] = useState<Set<string>>(new Set());
  const [elapsedTime, setElapsedTime] = useState(0);
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const { history, addHistory, removeHistory, clearHistory } = useQueryHistory();

  const specificationValidation = useMemo(
    () => validateSpecification(specification),
    [specification],
  );

  const canSubmit = Boolean(
    selectedDrug?.nameEn?.trim() &&
    selectedDosageForm &&
    selectedDatasources.length > 0 &&
    specificationValidation.isValid &&
    !loading
  );

  const enabledSourceSummary = useMemo(() => {
    const labels: Record<string, string> = {
      fda: "FDA",
      fda_guid: "FDA PSG",
      cde_reflisted: "CDE",
      cde_trials: "CDE",
      cde_yzxpj: "CDE",
      ema: "EMA",
      pmda: "PMDA",
      health_canada: "Health Canada",
      pubmed: "PubMed",
      pubchem: "PubChem",
      dailymed: "DailyMed",
      ich_m13a: "ICH M13A",
      nmpa: "NMPA",
      fda_review: "FDA Review",
    };
    const preferred = ["fda", "ema", "cde_reflisted", "pmda", "health_canada", "pubmed", "pubchem", "dailymed"];
    const enabledIds = availableDatasources.filter(ds => ds.enabled).map(ds => ds.id);
    const orderedIds = [
      ...preferred.filter(id => enabledIds.includes(id)),
      ...enabledIds.filter(id => !preferred.includes(id)),
    ];
    const uniqueLabels = Array.from(new Set(
      orderedIds.map(id => labels[id] || availableDatasources.find(ds => ds.id === id)?.name_en || id)
    ));
    return uniqueLabels.slice(0, 6).join(" · ") || "FDA · EMA · CDE · PubMed";
  }, [availableDatasources]);

  useEffect(() => {
    healthCheck().then(ok => setServerOk(ok)).catch(() => setServerOk(false));
  }, []);

  useEffect(() => {
    let cancelled = false;
    const fetchDsList = async () => {
      setDsRetrying(true);
      try {
        const data: DatasourcesResponse = await fetchDatasources();
        if (!cancelled) {
          setAvailableDatasources(data.datasources);
          setSelectedDatasources(data.datasources.filter(ds => ds.enabled).map(ds => ds.id));
          setDsLoadError(null);
          setUsingFallback(false);
        }
      } catch (err) {
        if (cancelled) return;
        const errMsg = err instanceof Error ? err.message : String(err);
        setDsLoadError(errMsg);
      } finally {
        if (!cancelled) setDsRetrying(false);
      }
    };
    fetchDsList();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    document.title = stage === "submit"
      ? "PharmaIntel - 智能药品查询"
      : stage === "progress"
        ? `PharmaIntel - 查询中... (${selectedDrug?.nameZh || ""})`
        : `PharmaIntel - ${task?.result?.drug_name || "查询结果"}`;
  }, [stage, selectedDrug?.nameZh, task?.result?.drug_name]);

  useEffect(() => {
    if (stage !== "progress" || !queryStartTime) return;
    elapsedRef.current = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - queryStartTime) / 1000));
    }, 1000);
    return () => {
      if (elapsedRef.current) clearInterval(elapsedRef.current);
    };
  }, [stage, queryStartTime]);

  const handleRetryDatasources = useCallback(async () => {
    setDsLoadError(null);
    setUsingFallback(false);
    setDsRetrying(true);
    try {
      const data: DatasourcesResponse = await fetchDatasources();
      setAvailableDatasources(data.datasources);
      setSelectedDatasources(data.datasources.filter(ds => ds.enabled).map(ds => ds.id));
      setDsLoadError(null);
    } catch (error) {
      const errMsg = error instanceof Error ? error.message : String(error);
      setDsLoadError(errMsg);
    } finally {
      setDsRetrying(false);
    }
  }, []);

  const handleUseFallback = useCallback(() => {
    setAvailableDatasources(FALLBACK_DATASOURCES);
    setSelectedDatasources(FALLBACK_DATASOURCES.filter(ds => ds.enabled).map(ds => ds.id));
    setDsLoadError(null);
    setUsingFallback(true);
  }, []);

  const findIconForDatasource = useCallback((dsId: string): string => {
    const ds = availableDatasources.find(d => d.id === dsId);
    return ds?.icon || '📊';
  }, [availableDatasources]);

  const handleCardToggle = useCallback((cardId: string) => {
    setExpandedCardId(prev => (prev === cardId ? null : cardId));
  }, []);

  const getDatasourceIcon = useCallback((dsId: string): React.ReactNode => {
    return DATASOURCE_ICONS[dsId] || findIconForDatasource(dsId);
  }, [findIconForDatasource]);

  const convertToProgressGridSources = useCallback((): ProgressGridSource[] => {
    return dataSourceStatuses.map(ds => ({
      id: ds.id,
      name: DATASOURCE_NAMES[ds.id] || ds.name,
      region: DATASOURCE_REGIONS[ds.id],
      icon: getDatasourceIcon(ds.id),
      status: ds.status as ProgressGridSource["status"],
      message: ds.message || '',
      duration: ds.elapsed,
    }));
  }, [dataSourceStatuses, getDatasourceIcon]);

  const toggleDatasource = useCallback((dsId: string) => {
    setSelectedDatasources(prev =>
      prev.includes(dsId) ? prev.filter(id => id !== dsId) : [...prev, dsId]
    );
  }, []);

  const selectAllDatasources = useCallback(() => {
    setSelectedDatasources(availableDatasources.filter(ds => ds.enabled).map(ds => ds.id));
  }, [availableDatasources]);

  const deselectAllDatasources = useCallback(() => {
    setSelectedDatasources([]);
  }, []);

  const validateForm = useCallback((): boolean => {
    const errors: FormErrors = {};
    if (!selectedDrug || !selectedDrug.nameEn.trim()) {
      errors.drugName = "请输入或选择一个有效的药品名称（至少2个字符）";
    }
    if (!selectedDosageForm) {
      errors.dosageForm = "请选择药品剂型";
    }
    if (!specificationValidation.isValid) {
      errors.specification = specificationValidation.error;
    }
    if (selectedDatasources.length === 0) {
      errors.datasources = "请至少选择一个数据源";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [selectedDrug, selectedDosageForm, selectedDatasources.length, specificationValidation]);

  const handleSubmit = useCallback(async () => {
    if (!validateForm()) return;
    setError(null);
    setStructuredError(null);
    setLoading(true);

    // 显示全局Loading（新功能）
    setShowGlobalLoading(true);
    setLoadingStage('submitting');

    setTask(null);
    setTaskId(null);
    setQueryStartTime(Date.now());
    setElapsedTime(0);

    console.log("[Query] 提交查询:", {
      drug_name: selectedDrug?.nameEn,
      dosage_form: selectedDosageForm?.name,
      specification: specificationValidation.normalized || undefined,
      selected_datasources: selectedDatasources,
      source_count: selectedDatasources?.length,
    });

    if (availableDatasources.length > 0) {
      const initialStatuses = availableDatasources
        .filter(ds => selectedDatasources.includes(ds.id))
        .map(ds => ({
        id: ds.id,
        name: ds.name,
        icon: ds.icon,
        status: "pending" as const,
        message: "初始化中...",
      }));
      setDataSourceStatuses(initialStatuses);
    } else {
      setDataSourceStatuses([]);
    }

    if (pollRef.current) clearInterval(pollRef.current);

    try {
      const { task_id } = await submitQuery(
        selectedDrug!.nameEn,
        selectedDatasources,
        selectedDrug!.nameZh,
        selectedDosageForm?.code || selectedDosageForm?.name,
        specificationValidation.normalized || undefined,
      );
      setTaskId(task_id);
      setStage("progress");
      setLoadingStage('querying');  // 切换到查询阶段
    } catch (e: unknown) {
      const errorMessage = e instanceof Error ? e.message : "提交失败";
      setError(errorMessage);
      setStructuredError(e instanceof Error ? e : new Error(String(e)));
      setLoading(false);
      setShowGlobalLoading(false);  // 隐藏Loading
    }
  }, [selectedDrug, selectedDosageForm, specificationValidation, selectedDatasources, availableDatasources, validateForm]);

  const handleCancel = useCallback(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    setLoading(false);
    setStage("submit");
    setError(null);
  }, []);

  const handleReset = useCallback(() => {
    setDrugInput("");
    setSelectedDrug(null);
    setSelectedDrugIdentity(null);
    setSelectedDosageForm(null);
    setSpecification("");
    setTask(null);
    setTaskId(null);
    setLoading(false);
    setError(null);
    setDataSourceStatuses([]);
    setQueryStartTime(null);
    setElapsedTime(0);
    setFormErrors({});
    setResultTab("all");
    setExpandedResultPanels(new Set());
    setStage("submit");
  }, []);

  const handleQuickDownload = useCallback(async (type: string) => {
    if (!task?.result) return;
    try {
      const blob = await downloadReport(task.result.task_id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${task.result.drug_name}_report.${type === "sources" ? "zip" : "md"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("下载失败:", err);
    }
  }, [task?.result]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Enter" && stage === "submit" && !showHistory) {
        e.preventDefault();
        handleSubmit();
      }
      if (e.key === "Escape") {
        if (showHistory) setShowHistory(false);
        else if (stage === "progress") handleCancel();
        else if (stage === "result") handleReset();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "d" && stage === "result") {
        e.preventDefault();
        handleQuickDownload("standard");
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [stage, showHistory, handleSubmit, handleCancel, handleReset, handleQuickDownload]);

  const handleDrugSelect = (drug: SelectableDrug) => {
    const mappedDrug = toDrugMapping(drug);
    setSelectedDrug(mappedDrug);
    setSelectedDrugIdentity(toDrugIdentityResult(drug, mappedDrug));
    setSpecification("");
    if (formErrors.drugName) {
      setFormErrors(prev => ({ ...prev, drugName: undefined }));
    }
  };

  const handleDosageFormDetected = useCallback((result: DosageFormMatchResult) => {
    setSelectedDosageForm(result.dosageForm);
    setSpecification("");
    if (formErrors.dosageForm) {
      setFormErrors(prev => ({ ...prev, dosageForm: undefined }));
    }
  }, [formErrors.dosageForm]);

  useEffect(() => {
    if (!taskId) return;
    let cancelled = false;

    const scheduleNextPoll = (delay: number) => {
      if (cancelled) return;
      pollRef.current = setTimeout(runPoll, delay);
    };

    const runPoll = async () => {
      try {
        const t = await pollTask(taskId);
        pollFailureCountRef.current = 0;
        setError(null);
        setStructuredError(null);
        setTask(t);
        if (t.sources_progress && Array.isArray(t.sources_progress) && t.sources_progress.length > 0) {
          setDataSourceStatuses(t.sources_progress.map((src: DSProgressType) => ({
            id: src.id,
            name: src.name,
            icon: findIconForDatasource(src.id),
            status: src.status,
            message: src.message || '',
            elapsed: src.elapsed,
          })));
        }
        if (t.done || t.status === "ERROR") {
          if (pollRef.current) clearInterval(pollRef.current);
          setLoading(false);
          setShowGlobalLoading(false);  // 隐藏全局Loading
          if (t.done && t.result && selectedDrug && selectedDosageForm) {
            addHistory({
              drug_name: t.result.drug_name || selectedDrug.nameEn,
              dosage_form: selectedDosageForm.name,
              task_id: taskId,
              status: t.status === "ERROR" ? "ERROR" : "DONE",
              summary: t.message,
            });
            setTimeout(() => setStage("result"), 500);
          } else if (t.status === "ERROR") {
            setError(t.message || "查询失败");
            setTimeout(() => setStage("submit"), 1500);
          }
          return;
        }
        scheduleNextPoll(1000);
      } catch (err) {
        console.error('Polling error:', err);
        pollFailureCountRef.current += 1;
        const failureCount = pollFailureCountRef.current;
        const errorValue = err instanceof Error ? err : new Error(String(err));

        if (failureCount >= 60) {
          setError("长时间无法连接后端，但后台任务不会被取消。请确认后端仍在运行后重新打开查询历史。");
          setStructuredError(errorValue);
          setLoading(false);
          setShowGlobalLoading(false);
          return;
        }

        setTask((previous) =>
          previous
            ? {
                ...previous,
                message: `连接暂时中断，正在自动重连（第 ${failureCount} 次）；后台查询仍在继续`,
              }
            : previous,
        );
        const retryDelay = Math.min(1000 * (2 ** Math.min(failureCount - 1, 3)), 10000);
        scheduleNextPoll(retryDelay);
      }
    };

    runPoll();
    return () => {
      cancelled = true;
      if (pollRef.current) clearTimeout(pollRef.current);
    };
  }, [taskId, findIconForDatasource, selectedDrug, selectedDosageForm, addHistory]);

  useEffect(() => {
    if (task?.status === "RUNNING" && task.message) {
      if (task.message.includes("AI") || task.message.includes("LLM")) {
        setLoadingStage('processing');
      } else if (task.message.includes("Word") || task.message.includes("报告")) {
        setLoadingStage('generating');
      } else {
        setLoadingStage('querying');
      }
    }
  }, [task?.status, task?.message]);

  const progressMessage = task?.status === "RUNNING"
    ? (task.message.includes("AI") || task.message.includes("LLM") ? "⏳ AI 整理中..."
      : task.message.includes("Word") || task.message.includes("报告") ? "⏳ 生成报告中..."
      : "⏳ 查询数据源中...")
    : null;

  const stats = useMemo(() => {
    const total = dataSourceStatuses.length;
    const success = dataSourceStatuses.filter(s => s.status === "success").length;
    const error = dataSourceStatuses.filter(s => s.status === "error").length;
    const notFound = dataSourceStatuses.filter(s => s.status === "not_found").length;
    const running = dataSourceStatuses.filter(s => s.status === "running").length;
    const pending = dataSourceStatuses.filter(s => s.status === "pending").length;
    const completed = success + error + notFound;
    const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;
    return { total, success, error, notFound, running, pending, completed, percentage };
  }, [dataSourceStatuses]);

  const qualityScore = useMemo(() => {
    if (!dataSourceStatuses.length) return 0;
    const successRate = (stats.success / stats.total) * 100;
    const coverageBonus = Math.min(stats.success * 5, 20);
    return Math.min(Math.round(successRate * 0.7 + coverageBonus + 10), 100);
  }, [dataSourceStatuses.length, stats]);

  const formatTime = (seconds: number): string => {
    if (seconds < 60) return `${seconds}s`;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}m${s}s`;
  };

  const toggleResultPanel = (id: string) => {
    setExpandedResultPanels(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filteredSourcesForTab = useMemo(() => {
    if (resultTab === "all") return dataSourceStatuses;
    if (resultTab === "success") return dataSourceStatuses.filter(s => s.status === "success");
    return dataSourceStatuses.filter(s => s.status === "error" || s.status === "not_found");
  }, [dataSourceStatuses, resultTab]);

  const summaryPreviewProps = useMemo(() => {
    if (!task?.result) return null;

    const result = task.result;
    const data = result.data || {};
    const fdaData = data.fda as Record<string, unknown> | undefined;
    const pubchemData = data.pubchem as Record<string, unknown> | undefined;
    const normalizedSpecification = specificationValidation.normalized || specification;

    const drugName = buildSummaryDrugName(result, selectedDrug);
    const dosageForm = firstSummaryValue(fdaData?.dosage_form, selectedDosageForm?.name, "—");
    const pkParams = buildSummaryPkParams(fdaData);
    const identityItems = buildSummaryIdentityItems(
        result,
        selectedDrug,
        selectedDosageForm,
        normalizedSpecification,
      );
    const approvalRows = buildSummaryApprovalRows(data);
    const evidenceLinks = buildSummaryEvidenceLinks(data);
    const isCombination = Boolean(pubchemData?.is_combination);
    const components = buildSummaryComponents(pubchemData, selectedDrug);
    const bioanalysisPhaseOne = buildBioanalysisPhaseOne({
      drugName,
      isCombination,
      components,
      data,
    });

    return {
      drugName,
      dosageForm,
      pkParams,
      identityItems,
      approvalRows,
      evidenceLinks,
      isCombination,
      components,
      phaseOneAnalysis: buildPhaseOneAnalysis({
        drugName,
        dosageForm,
        pkParams,
        identityItems,
        approvalRows,
        evidenceLinks,
        isCombination,
        components,
      }),
      bioanalysisPhaseOne,
      bioanalysisPhaseTwo: buildBioanalysisPhaseTwo({
        phaseOne: bioanalysisPhaseOne,
        data,
      }),
    };
  }, [
    task?.result,
    selectedDrug,
    selectedDosageForm,
    specification,
    specificationValidation.normalized,
  ]);

  return (
    <div className="min-h-screen bg-background flex flex-col overflow-x-hidden">
      {/* 全局Loading覆盖层（新功能） */}
      {showGlobalLoading && (
        <GlobalLoading
          stage={loadingStage}
          progress={stats.percentage}
          elapsedTime={elapsedTime}
          estimatedRemaining={stats.total > 0 && stats.completed < stats.total ? Math.max(0, (stats.total - stats.completed) * 5) : undefined}
          onCancel={handleCancel}
          dataSourceStatuses={dataSourceStatuses.map(ds => ({
            name: ds.name,
            status: ds.status,
            message: ds.message
          }))}
        />
      )}

      {/* 用户友好的错误提示（新功能） */}
      {structuredError && !showGlobalLoading && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg px-4">
          <UserFriendlyError
            error={structuredError}
            category={categorizeError(structuredError instanceof Error ? structuredError : new Error(String(structuredError)))}
            onRetry={() => {
              setStructuredError(null);
              setError(null);
              handleSubmit();
            }}
            onDismiss={() => {
              setStructuredError(null);
              setError(null);
            }}
            showDetails={true}
          />
        </div>
      )}
      {/* ===== Header ===== */}
      <header className="sticky top-0 z-40 h-[64px] bg-card/95 border-b border-border flex-shrink-0 shadow-sm backdrop-blur supports-[backdrop-filter]:bg-card/90">
        <div className="max-w-content mx-auto h-full px-4 sm:px-6 flex items-center justify-between">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="w-8 h-8 bg-primary/10 border border-primary/15 rounded-md flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M4 6h16M4 12h16M4 18h16" />
                  <circle cx="8" cy="6" r="2" fill="currentColor" />
                  <circle cx="16" cy="12" r="2" fill="currentColor" />
                  <circle cx="12" cy="18" r="2" fill="currentColor" />
                </svg>
              </div>
              <div className="hidden sm:block">
                <h1 className="text-base sm:text-lg font-semibold text-foreground tracking-tight leading-tight">
                  🧪 PharmaIntel
                </h1>
                <p className="text-[10px] sm:text-[11px] text-muted-foreground tracking-wide hidden md:block">Global Medicine Intelligence</p>
              </div>
            </div>
            {stage !== "submit" && (
              <div className="flex items-center gap-2 ml-2 pl-4 border-l border-border">
                <DrugChip drug={selectedDrug} />
                {selectedDosageForm && (
                  <span className="badge badge-info text-[10px] sm:text-xs hidden sm:inline-flex">
                    {selectedDosageForm.name}
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 sm:gap-4">
            <ThemeToggle />
            <div className="hidden sm:flex items-center gap-2 px-2.5 py-1 rounded-md bg-muted/70 border border-border">
              {serverOk === true && (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                  <span className="text-xs text-muted-foreground">已连接</span>
                </>
              )}
              {serverOk === false && (
                <>
                  <span className="w-1.5 h-1.5 rounded-full bg-red-400" />
                  <span className="text-xs text-muted-foreground">未连接</span>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ===== Main Content ===== */}
      <main className="flex-1 max-w-content mx-auto px-4 sm:px-6 py-6 sm:py-8 w-full">
        {/* Data source error banner */}
        {dsLoadError && (
          <div className="max-w-2xl mx-auto mb-6 animate-slideUp">
            <div className="card border-destructive/30 bg-destructive/5 p-5">
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <svg className="w-5 h-5 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-semibold text-foreground">数据源配置加载失败</h3>
                  <p className="text-xs text-muted-foreground mt-1 break-all">{dsLoadError}</p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button onClick={handleRetryDatasources} disabled={dsRetrying} className="btn btn-primary text-xs py-1.5 px-3">
                      {dsRetrying ? "重试中..." : "重新加载"}
                    </button>
                    <button onClick={handleUseFallback} className="btn text-xs py-1.5 px-3 border-border hover:bg-muted">
                      使用默认数据源（降级模式）
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {usingFallback && (
          <div className="max-w-2xl mx-auto mb-4 animate-fadeIn">
            <div className="card border-amber-500/30 bg-amber-500/5 p-3 flex items-center gap-2">
              <span className="text-amber-500 text-sm">⚠️</span>
              <span className="text-xs text-amber-700 dark:text-amber-400">
                当前运行在降级模式，数据源列表为预设默认值。
              </span>
              <button onClick={handleRetryDatasources} className="text-xs text-amber-600 dark:text-amber-400 underline ml-auto hover:no-underline">
                尝试恢复
              </button>
            </div>
          </div>
        )}

        {/* ==================== STAGE 1: Submit ==================== */}
        <div
          className={`transition-all duration-300 ease-out ${stage === "submit" ? "opacity-100 translate-y-0" : "opacity-0 absolute pointer-events-none -translate-y-4"}`}
          aria-hidden={stage !== "submit"}
        >
          {!showHistory ? (
            <div className="max-w-[880px] mx-auto space-y-6 animate-fadeIn">
              {/* Title */}
              <div className="text-center mb-8">
                <h2 className="text-2xl sm:text-3xl font-semibold text-foreground mb-3 tracking-tight">
                  🧪 PharmaIntel 智能药品查询
                </h2>
                <p className="text-sm sm:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
                  输入药品名称，一键查询 {enabledSourceSummary} 等全球权威数据源
                </p>
              </div>

              {/* Search Card */}
              <div className="card p-5 sm:p-8 animate-slideUp">
                <div className="space-y-6">
                  {/* Drug Name Input */}
                  <div className="animate-slideUp stagger-1">
                    <label className="flex items-center gap-2 text-base font-semibold text-foreground mb-2.5">
                      <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      药品名称
                      <span className="text-destructive">*</span>
                    </label>
                    <DrugInputWithSuggestions
                      value={drugInput}
                      onChange={(value, drug) => {
                        setDrugInput(value);
                        if (drug) {
                          const mappedDrug = toDrugMapping(drug);
                          setSelectedDrug(mappedDrug);
                          setSelectedDrugIdentity(toDrugIdentityResult(drug, mappedDrug));
                          setSpecification("");
                          if (formErrors.drugName) {
                            setFormErrors(prev => ({ ...prev, drugName: undefined }));
                          }
                        } else {
                          setSelectedDrug(null);
                          setSelectedDrugIdentity(null);
                        }
                      }}
                      onDrugSelect={handleDrugSelect}
                      onDosageFormDetected={handleDosageFormDetected}
                      placeholder="请输入药品名称（支持中英文，如：奥美拉唑/metformin）"
                    />
                    {formErrors.drugName && (
                      <p className="text-sm text-destructive mt-2 flex items-center gap-1.5 animate-scaleIn">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formErrors.drugName}
                      </p>
                    )}
                    {selectedDrug && (
                      <div className="mt-2.5 flex items-center gap-3 text-base animate-fadeIn">
                        <div className="flex items-center gap-2 text-success">
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <span className="font-medium">已选择</span>
                        </div>
                        <span className="text-foreground font-medium">
                          {selectedDrug.nameZh} ({selectedDrug.nameEn})
                        </span>
                        <span className="badge badge-info text-[10px]">{selectedDrug.category}</span>
                      </div>
                    )}
                    <DrugIdentityPreview identity={selectedDrugIdentity} drug={selectedDrug} />
                  </div>

                  {/* Dosage Form & Specification */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-slideUp stagger-2">
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                        剂型
                        <span className="text-destructive">*</span>
                      </label>
                      <DosageFormDropdown
                        value={selectedDosageForm}
                        onChange={(df) => {
                          setSelectedDosageForm(df);
                          setSpecification("");
                          if (formErrors.dosageForm) setFormErrors(prev => ({ ...prev, dosageForm: undefined }));
                        }}
                        onClear={() => setSelectedDosageForm(null)}
                      />
                      {formErrors.dosageForm && (
                        <p className="text-xs text-destructive mt-1.5 flex items-center gap-1 animate-scaleIn">
                          <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          {formErrors.dosageForm}
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="flex items-center gap-2 text-sm font-medium text-foreground mb-2">
                        规格
                        <span className="text-xs font-normal text-muted-foreground">（可选，手动输入）</span>
                      </label>
                      <SpecificationInput
                        drug={selectedDrug}
                        dosageForm={selectedDosageForm?.code || ''}
                        value={specification}
                        onChange={(value) => {
                          setSpecification(value);
                          if (formErrors.specification) {
                            setFormErrors(prev => ({ ...prev, specification: undefined }));
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Datasource Selection */}
                  <div className="animate-slideUp stagger-3">
                    <div className="flex items-center justify-between mb-3.5">
                      <label className="flex items-center gap-2.5 text-base font-semibold text-foreground">
                        <svg className="w-5 h-5 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                        </svg>
                        选择数据源
                        <span className="text-sm font-normal text-muted-foreground">(默认全选)</span>
                      </label>
                      <div className="flex gap-2.5">
                        <button onClick={selectAllDatasources} className="text-sm font-semibold text-primary hover:underline">全选</button>
                        <span className="text-border">|</span>
                        <button onClick={deselectAllDatasources} className="text-sm font-semibold text-primary hover:underline">清空</button>
                      </div>
                    </div>
                    {formErrors.datasources && (
                      <p className="text-sm text-destructive mb-2.5 flex items-center gap-1.5 animate-scaleIn">
                        <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {formErrors.datasources}
                      </p>
                    )}
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                      {availableDatasources.map(ds => {
                        const isSelected = selectedDatasources.includes(ds.id);

                        return (
                          <label
                            key={ds.id}
                            title={ds.name}
                            className={`datasource-item ${isSelected ? 'selected' : ''}`}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleDatasource(ds.id)}
                              className="sr-only"
                            />
                            <span className="ds-icon">
                              {ds.icon}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="ds-name">
                                {ds.name.split(' ')[0]}
                              </div>
                              <div className="ds-desc">
                                {ds.name.substring(ds.name.indexOf(' ') + 1) || '—'}
                              </div>
                            </div>
                            {isSelected && (
                              <div className="ds-check">
                                <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                              </div>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row gap-3 pt-2 animate-slideUp stagger-4">
                    <button
                      onClick={handleSubmit}
                      disabled={!canSubmit}
                      className="btn btn-primary w-full sm:w-auto text-base h-12 px-8 font-semibold gap-2 active:scale-[0.98]"
                    >
                      {loading ? (
                        <>
                          <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25"/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                          提交中...
                        </>
                      ) : (
                        <>
                          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          开始查询
                        </>
                      )}
                    </button>
                    <button
                      onClick={() => setShowHistory(!showHistory)}
                      className="btn btn-secondary w-full sm:w-auto gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      📜 查询历史
                    </button>
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-sm text-destructive bg-destructive/10 px-4 py-3 rounded animate-scaleIn">
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      {error}
                    </div>
                  )}
                </div>
              </div>

              <p className="text-center text-sm text-muted-foreground animate-fadeIn pt-3">
                数据来源：{enabledSourceSummary} 等 · 仅供信息汇总，不构成医疗建议
              </p>
            </div>
          ) : (
            /* History Panel */
            <div className="max-w-3xl mx-auto animate-fadeIn">
              <div className="flex items-center gap-3 mb-4">
                <button
                  onClick={() => setShowHistory(false)}
                  className="btn btn-ghost h-8 gap-1"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  返回
                </button>
                <h2 className="text-xl font-bold text-foreground">📜 查询历史</h2>
              </div>
              <QueryHistoryList
                history={history}
                onReQuery={(item) => {
                  const matched = drugMappings.find(
                    (d) => d.nameEn === item.drug_name || d.nameZh === item.drug_name
                  );
                  if (matched) {
                    setSelectedDrug(matched);
                    setSelectedDrugIdentity(toDrugIdentityResult(matched, matched));
                    setDrugInput(matched.nameZh);
                    const df = DOSAGE_FORMS.find((f) => f.name === item.dosage_form);
                    if (df) setSelectedDosageForm(df);
                    setShowHistory(false);
                    setTimeout(() => handleSubmit(), 100);
                  }
                }}
                onDelete={removeHistory}
                onClear={clearHistory}
              />
            </div>
          )}
        </div>

        {/* ==================== STAGE 2: Progress ==================== */}
        <div
          className={`transition-all duration-300 ease-out ${stage === "progress" ? "opacity-100 translate-y-0" : "opacity-0 absolute pointer-events-none translate-y-4"}`}
          aria-hidden={stage !== "progress"}
        >
          <div className="max-w-[1100px] mx-auto space-y-4 animate-fadeIn">
            {/* Status Header */}
            <div className="card p-4 flex items-center justify-between animate-slideDown">
              <div className="flex items-center gap-3">
                <button
                  onClick={handleCancel}
                  className="btn btn-ghost h-8 gap-1 text-sm"
                  title="返回修改 (Esc)"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                  返回
                </button>
                <div className="h-5 w-px bg-border" />
                <DrugChip drug={selectedDrug} />
                {selectedDosageForm && (
                  <span className="badge badge-info text-xs">{selectedDosageForm.name}</span>
                )}
              </div>
              <button
                onClick={handleCancel}
                className="btn btn-ghost h-8 gap-1 text-sm text-destructive hover:bg-destructive/10"
                title="取消查询 (Esc)"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                取消
              </button>
            </div>

            {/* Total Progress Bar */}
            <div className="card p-5 animate-slideUp" style={{ animationDelay: "100ms" }}>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-foreground">总体进度</span>
                  <div className="flex items-center gap-3">
                    <span className="text-lg font-bold text-primary">{stats.percentage}%</span>
                    <span className="text-sm text-muted-foreground">已用 {formatTime(elapsedTime)}</span>
                  </div>
                </div>
                <div className="w-full h-3 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all duration-700 ease-out relative"
                    style={{ width: `${stats.percentage}%` }}
                  >
                    <div className="absolute inset-0 bg-white/20 animate-pulse" />
                  </div>
                </div>
                {stats.running > 0 && (
                  <p className="text-xs text-muted-foreground">
                    💡 正在解析各国药品数据并生成报告...
                    {stats.total > 0 && stats.completed < stats.total && (
                      <> · 预估剩余 ~{Math.max(0, Math.round((stats.total - stats.completed) * 5))}s</>
                    )}
                  </p>
                )}
              </div>
            </div>

            {/* Progress Grid */}
            <ProgressGrid
              sources={convertToProgressGridSources()}
              expandedCardId={expandedCardId}
              onCardToggle={handleCardToggle}
            />

            {/* Stats Footer */}
            <div className="card p-4 animate-slideUp" style={{ animationDelay: "200ms" }}>
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-sm">
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />
                  <span className="text-muted-foreground">成功:</span>
                  <strong className="text-success">{stats.success}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
                  <span className="text-muted-foreground">失败:</span>
                  <strong className="text-destructive">{stats.error}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-amber-500" />
                  <span className="text-muted-foreground">无数据:</span>
                  <strong className="text-warning">{stats.notFound}</strong>
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
                  <span className="text-muted-foreground">查询中:</span>
                  <strong className="text-info">{stats.running}</strong>
                </span>
                <span className="text-muted-foreground">
                  共 <strong>{stats.total}</strong> 个数据源
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* ==================== STAGE 3: Result ==================== */}
        <div
          className={`transition-all duration-300 ease-out ${stage === "result" ? "opacity-100 translate-y-0" : "opacity-0 absolute pointer-events-none translate-y-4"}`}
          aria-hidden={stage !== "result"}
        >
          {task?.done && task.result && (
            <div className="max-w-[1200px] mx-auto space-y-4 animate-fadeIn">
              {/* Sticky Summary Banner */}
              <div className="sticky top-[64px] z-30 card p-4 sm:p-5 animate-slideDown">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="w-8 h-8 rounded-md bg-success/10 border border-success/20 flex items-center justify-center">
                        <svg className="w-5 h-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </span>
                      <div>
                        <span className="font-semibold text-foreground text-sm sm:text-base">查询完成</span>
                        <span className="text-muted-foreground text-xs sm:text-sm ml-2">
                          用时: {formatTime(elapsedTime)}
                        </span>
                      </div>
                    </div>
                    <QualityScoreRing score={qualityScore} />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <button
                      onClick={handleSubmit}
                      className="btn btn-secondary text-xs h-8 gap-1"
                      title="重新查询"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      重查
                    </button>
                    <DownloadPanel
                      taskId={task.result.task_id}
                      drugName={task.result.drug_name}
                      hasLlmReport={!!task.result.report_text && task.result.llm_status === "完成"}
                      onDownloadStart={(type) => console.log(`[Download] Started: ${type}`)}
                      onDownloadComplete={(type) => console.log(`[Download] Complete: ${type}`)}
                      onError={(err) => console.error(`[Download] Error:`, err)}
                    />
                    <button
                      onClick={handleReset}
                      className="btn btn-ghost text-xs h-8 gap-1"
                      title="返回首页"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      首页
                    </button>
                  </div>
                </div>
              </div>

              {/* Result Tabs */}
              <div className="card p-1 inline-flex gap-1 rounded-md bg-muted/30">
                {([
                  { key: "all" as const, label: "全部结果", count: dataSourceStatuses.length },
                  { key: "success" as const, label: "✅ 成功", count: stats.success },
                  { key: "issues" as const, label: "❌ 问题", count: stats.error + stats.notFound },
                ]).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setResultTab(tab.key)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all duration-150 ${
                      resultTab === tab.key
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
                    }`}
                  >
                    {tab.label}
                    {tab.count > 0 && (
                      <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                        tab.key === "success" ? "bg-success/15 text-success" :
                        tab.key === "issues" ? "bg-destructive/15 text-destructive" :
                        "bg-muted-foreground/15 text-muted-foreground"
                      }`}>
                        {tab.count}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* Summary Preview */}
              {summaryPreviewProps && (
                <SummaryPreviewCard
                  drugName={summaryPreviewProps.drugName}
                  dosageForm={summaryPreviewProps.dosageForm}
                  pkParams={summaryPreviewProps.pkParams}
                  sourceCoverage={{ covered: stats.success + stats.notFound, total: stats.total }}
                  qualityScore={qualityScore}
                  isCombination={summaryPreviewProps.isCombination}
                  components={summaryPreviewProps.components}
                  identityItems={summaryPreviewProps.identityItems}
                  approvalRows={summaryPreviewProps.approvalRows}
                  evidenceLinks={summaryPreviewProps.evidenceLinks}
                  phaseOneAnalysis={summaryPreviewProps.phaseOneAnalysis}
                  onDownload={async () => {
                    if (!task.result) return;
                    const blob = await downloadReport(task.result.task_id);
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${task.result.drug_name}_report.docx`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                />
              )}

              {summaryPreviewProps && (
                <BioanalysisPhaseOne analysis={summaryPreviewProps.bioanalysisPhaseOne} />
              )}

              {summaryPreviewProps && (
                <BioanalysisPhaseTwo analysis={summaryPreviewProps.bioanalysisPhaseTwo} />
              )}

              {/* Datasource Panels */}
              <div className="space-y-3">
                {filteredSourcesForTab.map(ds => {
                  const dsKey = ds.id;
                  return (
                    <DatasourcePanel
                      key={dsKey}
                      id={dsKey}
                      name={DATASOURCE_NAMES[dsKey] || ds.name}
                      icon={getDatasourceIcon(dsKey)}
                      status={ds.status}
                      duration={ds.elapsed}
                      data={(task.result?.data as Record<string, Record<string, unknown>>)?.[dsKey]}
                      drugName={task.result?.drug_name}
                      isExpanded={expandedResultPanels.has(dsKey)}
                      onToggle={() => toggleResultPanel(dsKey)}
                    />
                  );
                })}
              </div>

              {/* Detailed Results */}
              <div className="pt-2">
                <ResultRenderer result={task.result} selectedDatasources={selectedDatasources} />
              </div>
            </div>
          )}
        </div>
      </main>

      {/* ===== Footer ===== */}
      <footer className="border-t border-border bg-card/70 py-4 mt-auto flex-shrink-0">
        <div className="max-w-content mx-auto px-4 sm:px-6 text-center text-xs text-muted-foreground">
          PharmaIntel v0.1 · 智能医药情报平台 · 仅供信息汇总，不构成医疗建议
        </div>
      </footer>

      {/* Mobile Bottom Nav */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 h-[60px] bg-card border-t border-border shadow-[0_-2px_10px_rgba(0,0,0,0.08)] flex items-center justify-around z-50">
        <button
          onClick={() => { handleReset(); }}
          className={`flex flex-col items-center gap-0.5 min-w-[44px] min-h-[44px] justify-center ${stage === "submit" ? "text-primary" : "text-muted-foreground"}`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="text-[10px]">查询</span>
        </button>
        <button
          onClick={() => { if (stage === "submit" && canSubmit) handleSubmit(); }}
          className={`flex flex-col items-center gap-0.5 min-w-[44px] min-h-[44px] justify-center ${stage === "progress" ? "text-primary" : "text-muted-foreground"}`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
          <span className="text-[10px]">进度</span>
        </button>
        <button
          className={`flex flex-col items-center gap-0.5 min-w-[44px] min-h-[44px] justify-center ${stage === "result" ? "text-primary" : "text-muted-foreground"}`}
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <span className="text-[10px]">结果</span>
        </button>
      </nav>
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}
